import { IPagination } from '../../../types/contactModule';

/**
 * DEV-ONLY fixture data for `environment.devMockApi` (`ng serve --configuration mock`).
 *
 * One small Botswana tenant whose parts agree with each other:
 * - 47 contacts with +267 numbers in 12 organisations, 8 tags and 5 segments.
 *   Segment members really match their saved rules.
 * - 40 messages (34 broadcasts plus the 6 per-row records one smart broadcast
 *   leaves behind) and 12 campaigns covering every campaign status.
 * - Per-recipient delivery receipts for every send, generated on demand, so
 *   report rows, charts, counts, campaign stats, the SMS gateway log and
 *   mockDelivery() all tell the same story.
 * - Templates, sender IDs, test contacts, team members, USSD applications with
 *   promotions and session records, and a scripted USSD simulator menu.
 *
 * Deterministic: every block draws from its own seeded stream (never
 * Math.random), so reloads look the same and editing one block never
 * reshuffles another. Only "now" moves: dates are relative to page load.
 *
 * The interceptor is registered in every build, so this module is bundled
 * everywhere but read only by the `mock` configuration. Module load stays
 * cheap; receipt rows are computed per request.
 */

export const MOCK_ORG_ID = '00000000-0000-4000-8000-000000000001';
export const MOCK_USER_ID = '11111111-1111-4111-8111-111111111111';
export const MOCK_ORG_NAME = 'Black Konnect (Dev)';

export interface MockUserRef {
  id: string;
  login: string;
}

const ME: MockUserRef = { id: MOCK_USER_ID, login: 'dev.user' };
/** Who composes messages, so lists show more than one author. */
const TEAM: MockUserRef[] = [
  ME,
  { id: 'user-0002', login: 'n.sebego' },
  { id: 'user-0004', login: 'm.moremi' },
];

// ------------------------------------------------------------------ PRNG

/** Mulberry32 — small deterministic PRNG. */
function seeded(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** An independent stream per fixture block. */
function stream(seed: number) {
  const rand = seeded(seed);
  const int = (min: number, max: number) =>
    min + Math.floor(rand() * (max - min + 1));
  const pick = <T>(list: readonly T[]): T =>
    list[Math.floor(rand() * list.length)];
  return { rand, int, pick };
}

/** Stable 32-bit hash of a string. */
export function hashOf(value: string): number {
  let hash = 0;
  for (const char of value) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash;
}

/** Well-mixed 32-bit hash of two integers, for per-row decisions. */
function mix(a: number, b = 0): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be5ab, 0xc2b2ae35);
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

// ------------------------------------------------------------------ time

const NOW = Date.now();
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** ISO timestamp `days` in the past (negative = future). */
const ago = (days: number) => new Date(NOW - days * DAY).toISOString();
/** ISO timestamp `minutes` in the past; day granularity would give every row
 *  the same clock time, which looks broken in a dispatch log. */
const agoMinutes = (minutes: number) =>
  new Date(NOW - minutes * MINUTE).toISOString();
const iso = (ms: number) => new Date(ms).toISOString();
const pad = (n: number) => String(n).padStart(2, '0');
const pad4 = (n: number) => String(n).padStart(4, '0');

/** `YYYY-MM-DD` and `HH:mm` in local time, the shapes the compose form submits. */
function scheduleParts(value: string | number) {
  const d = new Date(value);
  return {
    scheduleDate: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    scheduleTime: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function yearsSince(date: string): number {
  return Math.floor((NOW - Date.parse(date)) / (365.25 * DAY));
}

const round1 = (n: number) => Math.round(n * 10) / 10;
const share = (part: number, whole: number) => (whole ? round1((part / whole) * 100) : 0);

// ------------------------------------------------------------------ names

const FIRST_NAMES = [
  'Kagiso', 'Naledi', 'Tebogo', 'Boitumelo', 'Mpho', 'Lesego', 'Thato',
  'Onalenna', 'Goitseone', 'Kefilwe', 'Tumelo', 'Bonolo', 'Neo', 'Amantle',
  'Katlego', 'Reneilwe', 'Oarabile', 'Gaone', 'Tshepo', 'Masego',
];
const LAST_NAMES = [
  'Molefe', 'Sebego', 'Kgosi', 'Moremi', 'Tau', 'Dikgang', 'Mmusi',
  'Serame', 'Motswana', 'Ramotswa', 'Phiri', 'Segwai', 'Baruti', 'Kwenaite',
];
const CITIES = [
  'Gaborone', 'Francistown', 'Maun', 'Kasane', 'Palapye', 'Serowe',
  'Lobatse', 'Selebi-Phikwe', 'Mochudi', 'Molepolole',
];
const DISTRICTS: Record<string, string> = {
  Gaborone: 'South-East',
  Francistown: 'North-East',
  Maun: 'North-West',
  Kasane: 'Chobe',
  Palapye: 'Central',
  Serowe: 'Central',
  Lobatse: 'South-East',
  'Selebi-Phikwe': 'Central',
  Mochudi: 'Kgatleng',
  Molepolole: 'Kweneng',
};
const districtOf = (city: string) => DISTRICTS[city] ?? 'South-East';

/** Botswana mobile, +267 7XXXXXXX. Stable per index and unique below 9,000,000. */
export function phone(index: number): string {
  const subscriber = ((index * 7919) % 9000000) + 1000000;
  return `+2677${subscriber}`;
}

// ---------------------------------------------------------- query helpers

export function emptyPagination(pageSize = 10): IPagination {
  return {
    totalElements: 0,
    pageSize,
    totalPages: 0,
    pageNumber: 0,
    offSet: 0,
  };
}

/** Page and size from Spring (`page`, `size`) or portal (`pageNumber`, `pageSize`) params. */
export function pageOf(params: URLSearchParams, defaultSize = 10) {
  const size = Number(params.get('size') ?? params.get('pageSize') ?? defaultSize);
  const page = Number(params.get('page') ?? params.get('pageNumber') ?? 0);
  const safeSize = Number.isFinite(size) && size > 0 ? Math.floor(size) : defaultSize;
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 0;
  return { page: safePage, size: safeSize, offset: safePage * safeSize };
}

/** The `{ items, itemPagination }` envelope (GenericResponse). */
export function paginate<T>(all: T[], params: URLSearchParams, defaultSize = 10) {
  const { page, size, offset } = pageOf(params, defaultSize);
  return {
    items: all.slice(offset, offset + size),
    itemPagination: {
      totalElements: all.length,
      pageSize: size,
      totalPages: Math.max(1, Math.ceil(all.length / size)),
      pageNumber: page,
      offSet: offset,
    } as IPagination,
  };
}

/** One Spring page for endpoints that answer a bare array plus `X-Total-Count`. */
export function pageSlice<T>(all: T[], params: URLSearchParams, defaultSize = 20): T[] {
  const { size, offset } = pageOf(params, defaultSize);
  return all.slice(offset, offset + size);
}

export type Getter = (item: any, field: string) => unknown;
const plainGet: Getter = (item, field) =>
  item && typeof item === 'object' ? item[field] : undefined;

const PAGING_KEYS = new Set([
  'page', 'size', 'pageSize', 'pageNumber', 'sort', 'order', 'sortBy', 't',
  'distinct', 'eagerload',
]);
/** The mock serves one organisation, so tenant filters always match. */
const TENANT_FIELD = /^(organisationId|organisationUuId|organisationUuid|organizationId|organizationUuid)$/i;

/** Every value of a key: repeated keys and comma-joined lists both count. */
export function listValues(params: URLSearchParams, key: string): string[] {
  return params
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);
}

function keysOf(params: URLSearchParams): string[] {
  const keys = new Set<string>();
  params.forEach((_, key) => keys.add(key));
  return [...keys];
}

/** Numbers, booleans and ISO dates compare as numbers; other text in lower case. */
function comparable(value: unknown): number | string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const text = String(value);
  if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const time = Date.parse(text);
    if (!Number.isNaN(time)) return time;
  }
  return text.toLowerCase();
}

function sameValue(value: unknown, wanted: string): boolean {
  if (Array.isArray(value)) return value.some((item) => sameValue(item, wanted));
  if (value == null) return false;
  return String(value).toLowerCase() === wanted.toLowerCase();
}

const textOf = (value: unknown) =>
  (Array.isArray(value) ? value.join(' ') : String(value ?? '')).toLowerCase();

/** One JHipster criteria operator (`field.op=raw`) against a value. */
export function testCriterion(
  value: unknown,
  op: string,
  raw: string,
  params: URLSearchParams,
  key: string
): boolean {
  switch (op) {
    case 'equals':
      return sameValue(value, raw);
    case 'notEquals':
      return !sameValue(value, raw);
    case 'contains':
      return textOf(value).includes(raw.toLowerCase());
    case 'doesNotContain':
      return !textOf(value).includes(raw.toLowerCase());
    case 'in':
      return listValues(params, key).some((wanted) => sameValue(value, wanted));
    case 'notIn':
      return !listValues(params, key).some((wanted) => sameValue(value, wanted));
    case 'specified':
      // Mock flags default to false where the real rows leave them null, so
      // "archive.specified=false" (the Active tab) still matches them.
      return (raw === 'true') === (value != null && value !== '' && value !== false);
    case 'greaterThan':
    case 'greaterThanOrEqual':
    case 'lessThan':
    case 'lessThanOrEqual': {
      const left = comparable(value);
      const right = comparable(raw);
      if (left == null || right == null || typeof left !== typeof right) return false;
      if (op === 'greaterThan') return left > right;
      if (op === 'greaterThanOrEqual') return left >= right;
      if (op === 'lessThan') return left < right;
      return left <= right;
    }
    default:
      return true;
  }
}

/**
 * Does an item pass every `field.op` criterion in the query? Unknown fields
 * are ignored, as the API ignores criteria it does not support.
 */
export function matchesCriteria(
  item: unknown,
  params: URLSearchParams,
  get: Getter = plainGet,
  skip: ReadonlySet<string> = new Set()
): boolean {
  for (const key of keysOf(params)) {
    if (PAGING_KEYS.has(key) || skip.has(key)) continue;
    const dot = key.lastIndexOf('.');
    if (dot < 1) continue;
    const field = key.slice(0, dot);
    if (TENANT_FIELD.test(field)) continue;
    const value = get(item, field);
    if (value === undefined && !(item && typeof item === 'object' && field in item)) continue;
    if (!testCriterion(value, key.slice(dot + 1), params.get(key) ?? '', params, key)) {
      return false;
    }
  }
  return true;
}

/** Spring `sort=field,dir` (repeatable), with an optional default. */
export function sortList<T>(
  list: T[],
  params: URLSearchParams,
  fallback?: string,
  get: Getter = plainGet
): T[] {
  const specs = params.getAll('sort').filter(Boolean);
  const rules = (specs.length ? specs : fallback ? [fallback] : []).map((spec) => {
    const [field, dir] = spec.split(',');
    return { field: field.trim(), desc: (dir ?? '').trim().toLowerCase() === 'desc' };
  });
  if (!rules.length) return list;

  return [...list].sort((a, b) => {
    for (const { field, desc } of rules) {
      const left = comparable(get(a, field));
      const right = comparable(get(b, field));
      if (left === right) continue;
      if (left == null) return 1;
      if (right == null) return -1;
      const order = left < right ? -1 : 1;
      return desc ? -order : order;
    }
    return 0;
  });
}

/** Filter by criteria, then sort. */
export function query<T>(
  list: T[],
  params: URLSearchParams,
  options: { sort?: string; get?: Getter; skip?: ReadonlySet<string> } = {}
): T[] {
  const get = options.get ?? plainGet;
  return sortList(
    list.filter((item) => matchesCriteria(item, params, get, options.skip)),
    params,
    options.sort,
    get
  );
}

/** Find by uuid, numeric id or any listed alias. */
function byKey<T extends { id: number | string; uuid?: string }>(list: T[], key: string): T | undefined {
  return list.find((item) => item.uuid === key || String(item.id) === key);
}

// ---------------------------------------------------------------- contacts

export interface MockRef {
  id: number;
  uuid: string;
  name: string;
}

const STREETS = [
  'Kgale View', 'Phakalane', 'Broadhurst', 'Block 8', 'Extension 12',
  'Tlokweng Road', 'Mogoditshane', 'Village',
];

const contactRand = stream(1101);

export const MOCK_CONTACTS = Array.from({ length: 47 }, (_, i) => {
  const firstName = FIRST_NAMES[i % FIRST_NAMES.length];
  const lastName = LAST_NAMES[(i * 3) % LAST_NAMES.length];
  const city = CITIES[i % CITIES.length];
  const birthDate = ago(contactRand.int(6570, 18250)).slice(0, 10);
  const createdDays = contactRand.int(1, 300);

  return {
    id: i + 1,
    uuid: `contact-${pad4(i + 1)}`,
    externalId: null as string | null,
    firstName,
    middleName: null as string | null,
    lastName,
    fullName: `${firstName} ${lastName}`,
    nickName: '',
    address: `Plot ${contactRand.int(100, 74000)}, ${contactRand.pick(STREETS)}`,
    addressLine2: null as string | null,
    region: districtOf(city),
    zipCode: '00267',
    city,
    country: 'Botswana',
    countryCode: 'BW',
    gender: i % 2 === 0 ? 'FEMALE' : 'MALE',
    birthDate,
    age: yearsSince(birthDate),
    profilePicture: null as string | null,
    primaryContactNumber: phone(i + 1),
    emailAddress: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.co.bw`,
    origin: contactRand.pick(['CSV_IMPORT', 'MANUAL', 'API']),
    modifiedFrom: null as string | null,
    identityId: null as string | null,
    customerStage: contactRand.pick(['LEAD', 'PROSPECT', 'CUSTOMER', 'CHURNED']),
    type: 'CONTACT',
    entityStatusType: 'ACTIVE',
    status: {} as Record<string, unknown>,
    createdAt: ago(createdDays),
    modifiedAt: ago(contactRand.int(0, Math.min(30, createdDays))),
    recordStatus: 'ACTIVE',
    organisationId: MOCK_ORG_ID,
    entityTags: [] as MockRef[],
    companyEntities: [] as MockRef[],
    entitySegments: [] as MockRef[],
    contactDetails: [] as unknown[],
    customAttributes: [] as unknown[],
    notes: [] as unknown[],
  };
});

export type MockContact = (typeof MOCK_CONTACTS)[number];

// --------------------------------------------------------------- companies

const COMPANY_PLAN: [string, string, string][] = [
  ['Kalahari Retail Group', 'RETAIL', 'Supermarket chain with 40 stores'],
  ['Okavango Logistics', 'TRANSPORTATION_AND_LOGISTICS', 'Cross-border trucking and warehousing'],
  ['Gaborone Fintech', 'FINANCE', 'Mobile payments and micro-loans'],
  ['Chobe Safari Holdings', 'HOSPITALITY', 'Lodges and river safaris in Kasane'],
  ['Delta Insurance', 'FINANCE', 'Short-term and funeral cover'],
  ['Botswana Agri Co-op', 'AGRICULTURE', 'Cattle and crop farmers co-operative'],
  ['Phakalane Property', 'REAL_ESTATE', 'Residential estates north of Gaborone'],
  ['Tlokweng Motors', 'AUTOMOTIVE', 'New and used vehicle dealership'],
  ['Limpopo Freight', 'TRANSPORTATION_AND_LOGISTICS', 'Rail and road freight forwarding'],
  ['Serowe Health Group', 'HEALTHCARE', 'Private clinics in the Central district'],
  ['Orapa Mining Services', 'MINING_AND_METALS', 'Contract services for diamond mines'],
  ['Maun Tourism Board', 'HOSPITALITY', 'Destination marketing for the Okavango Delta'],
];

const companyRand = stream(1201);

export const MOCK_COMPANIES = COMPANY_PLAN.map(([name, industry, description], i) => {
  const city = CITIES[i % CITIES.length];
  const slug = name.toLowerCase().replace(/[^a-z]+/g, '');
  return {
    id: i + 1,
    uuid: `company-${pad4(i + 1)}`,
    name,
    description,
    emailAddress: `hello@${slug}.co.bw`,
    primaryContactNumber: phone(500 + i),
    address: `Plot ${companyRand.int(100, 69000)}, ${city}`,
    addressLine2: null as string | null,
    city,
    region: districtOf(city),
    zipCode: '00267',
    country: 'Botswana',
    countryCode: 'BW',
    industry,
    customerStage: i % 3 === 2 ? 'PROSPECTIVE' : 'CUSTOMER',
    registrationNumber: `BW000${companyRand.int(1000000, 9999999)}`,
    vatNumber: null as string | null,
    origin: 'MANUAL',
    contactsCount: 0,
    type: 'COMPANY',
    entityStatusType: 'ACTIVE',
    recordStatus: 'ACTIVE',
    organisationId: MOCK_ORG_ID,
    createdAt: ago(companyRand.int(30, 400)),
    modifiedAt: ago(companyRand.int(0, 25)),
  };
});

export type MockCompany = (typeof MOCK_COMPANIES)[number];

// -------------------------------------------------------------------- tags

const TAG_PLAN: [string, string][] = [
  ['VIP Customers', 'Top spenders on the loyalty programme'],
  ['Newsletter Opt-in', 'Opted in to the monthly newsletter'],
  ['Trial Users', 'Started a free trial in the last 60 days'],
  ['Churn Risk', 'Flagged by the retention model'],
  ['Gaborone Region', 'Registered in Greater Gaborone'],
  ['Enterprise', 'Business accounts with five or more lines'],
  ['Prepaid', 'Prepaid subscribers'],
  ['Postpaid', 'Postpaid subscribers'],
];

const tagRand = stream(1301);

export const MOCK_TAGS = TAG_PLAN.map(([name, description], i) => ({
  id: i + 1,
  uuid: `tag-${pad4(i + 1)}`,
  name,
  description,
  contactsCount: 0,
  entityStatusType: 'ACTIVE',
  recordStatus: 'ACTIVE',
  organisationId: MOCK_ORG_ID,
  createdBy: ME,
  createdAt: ago(tagRand.int(10, 260)),
  modifiedAt: ago(tagRand.int(0, 9)),
}));

export type MockTag = (typeof MOCK_TAGS)[number];

/** Who carries each tag, in tag order. */
const TAG_RULES: ((contact: MockContact, i: number) => boolean)[] = [
  (_, i) => mix(i, 11) % 100 < 18,
  (_, i) => mix(i, 12) % 100 < 50,
  (_, i) => mix(i, 13) % 100 < 22,
  (_, i) => mix(i, 14) % 100 < 16,
  (contact) => contact.city === 'Gaborone' || contact.city === 'Mochudi',
  (_, i) => mix(i, 16) % 100 < 14,
  (_, i) => mix(i, 17) % 100 < 64,
  (_, i) => mix(i, 17) % 100 >= 64,
];

// ---------------------------------------------------------------- segments

type SegmentDataType = 'TEXT' | 'DATE' | 'NUMBER';

const SEGMENT_PLAN: {
  name: string;
  description: string;
  rules: [attribute: string, op: string, value: string, type: SegmentDataType][];
}[] = [
  {
    name: 'Joined in the last 90 days',
    description: 'Contacts created in the last 90 days',
    rules: [['createdAt', 'greaterThan', ago(90).slice(0, 10), 'DATE']],
  },
  {
    name: 'Gaborone residents',
    description: 'Contacts whose city is Gaborone',
    rules: [['city', 'equals', 'Gaborone', 'TEXT']],
  },
  {
    name: 'Women under 36',
    description: 'Female contacts aged 35 or younger',
    rules: [
      ['gender', 'equals', 'FEMALE', 'TEXT'],
      ['Age', 'lessThan', '36', 'NUMBER'],
    ],
  },
  {
    name: 'Northern Botswana',
    description: 'Contacts in Francistown, Maun or Kasane',
    rules: [['city', 'in', 'Francistown,Maun,Kasane', 'TEXT']],
  },
  {
    name: 'Over 40',
    description: 'Contacts older than 40',
    rules: [['Age', 'greaterThan', '40', 'NUMBER']],
  },
];

const segmentRand = stream(1401);
let segmentFilterId = 1;

export const MOCK_SEGMENTS = SEGMENT_PLAN.map((plan, i) => ({
  id: i + 1,
  uuid: `segment-${pad4(i + 1)}`,
  name: plan.name,
  description: plan.description,
  contactsCount: 0,
  // The shape segment-utilities.ts converts for the rule editor.
  entitySegmentQueryFilters: plan.rules.map(([attributeName, op, filterValue, dataType], order) => {
    const id = segmentFilterId++;
    return {
      id,
      uuid: `segment-filter-${pad4(id)}`,
      attributeName,
      propertyFilterParameter: `${attributeName}.${op}`,
      filterValue,
      dataType,
      objectType: 'PERSON_ENTITY',
      order,
      filterSpecification: 'AND_OPERATOR' as const,
      organisationId: MOCK_ORG_ID,
      recordStatus: 'ACTIVE',
    };
  }),
  entityStatusType: 'ACTIVE',
  recordStatus: 'ACTIVE',
  organisationId: MOCK_ORG_ID,
  createdBy: ME,
  modifiedBy: ME,
  createdAt: ago(segmentRand.int(15, 200)),
  modifiedAt: ago(segmentRand.int(0, 14)),
}));

export type MockSegment = (typeof MOCK_SEGMENTS)[number];
type SegmentFilter = MockSegment['entitySegmentQueryFilters'][number];

/** The segment builder saves the contacts service's own keys ("gender.is not"). */
const SEGMENT_OPS: Record<string, string> = {
  is: 'equals',
  'is not': 'notEquals',
  'not contain': 'doesNotContain',
  'will be in': 'equals',
};

function segmentFilterMatches(contact: MockContact, filter: SegmentFilter): boolean {
  const saved = filter.propertyFilterParameter.split('.')[1] ?? 'equals';
  const op = SEGMENT_OPS[saved] ?? saved;
  const value =
    filter.attributeName === 'Age'
      ? contact.age
      : (contact as unknown as Record<string, unknown>)[filter.attributeName];
  const probe = new URLSearchParams();
  probe.append('rule', filter.filterValue);
  return testCriterion(value, op, filter.filterValue, probe, 'rule');
}

// ------------------------------------------------------------- memberships

/** Organisations a contact (by position) belongs to; a few belong to two, some to none. */
function companiesOf(i: number): number[] {
  const list: number[] = [];
  if (i % 4 !== 3) list.push(i % MOCK_COMPANIES.length);
  if (i % 9 === 0) list.push((i + 5) % MOCK_COMPANIES.length);
  return list;
}

export type GroupKind = 'companies' | 'tags' | 'segments';

const MEMBERS: Record<GroupKind, Map<string, MockContact[]>> = {
  companies: new Map(),
  tags: new Map(),
  segments: new Map(),
};

const toRef = (group: { id: number; uuid: string; name: string }): MockRef => ({
  id: group.id,
  uuid: group.uuid,
  name: group.name,
});

MOCK_COMPANIES.forEach((company, k) => {
  const members = MOCK_CONTACTS.filter((_, i) => companiesOf(i).includes(k));
  company.contactsCount = members.length;
  members.forEach((contact) => contact.companyEntities.push(toRef(company)));
  MEMBERS.companies.set(company.uuid, members);
});

MOCK_TAGS.forEach((tag, t) => {
  const members = MOCK_CONTACTS.filter((contact, i) => TAG_RULES[t](contact, i));
  tag.contactsCount = members.length;
  members.forEach((contact) => contact.entityTags.push(toRef(tag)));
  MEMBERS.tags.set(tag.uuid, members);
});

MOCK_SEGMENTS.forEach((segment) => {
  const members = MOCK_CONTACTS.filter((contact) =>
    segment.entitySegmentQueryFilters.every((filter) => segmentFilterMatches(contact, filter))
  );
  segment.contactsCount = members.length;
  members.forEach((contact) => contact.entitySegments.push(toRef(segment)));
  MEMBERS.segments.set(segment.uuid, members);
});

export const findContact = (key: string) => byKey(MOCK_CONTACTS, key);
export const findCompany = (key: string) => byKey(MOCK_COMPANIES, key);
export const findTag = (key: string) => byKey(MOCK_TAGS, key);
export const findSegment = (key: string) => byKey(MOCK_SEGMENTS, key);

function findGroup(kind: GroupKind, key: string) {
  if (kind === 'companies') return findCompany(key);
  if (kind === 'tags') return findTag(key);
  return findSegment(key);
}

/** Contacts in an organisation, tag or segment (by uuid or id). */
export function groupMembers(kind: GroupKind, key: string): MockContact[] {
  const group = findGroup(kind, key);
  return group ? MEMBERS[kind].get(group.uuid) ?? [] : [];
}

/** Criteria getter for contacts: group-membership criteria test the contact's groups. */
export const contactGet: Getter = (contact, field) => {
  const refs = (list: MockRef[]) => list.flatMap((ref) => [ref.id, ref.uuid]);
  switch (field) {
    case 'entityTagId':
    case 'entityTagUuid':
      return refs(contact.entityTags);
    case 'companyEntityId':
    case 'companyEntityUuid':
      return refs(contact.companyEntities);
    case 'entitySegmentId':
    case 'entitySegmentUuid':
      return refs(contact.entitySegments);
    default:
      return plainGet(contact, field);
  }
};

/** What a new contact looks like once the API has saved it. */
export function newContact(body: any, index = 0) {
  const id = 1000 + index;
  const firstName = String(body?.firstName ?? '');
  const lastName = String(body?.lastName ?? '');
  return {
    ...body,
    id,
    uuid: `contact-new-${pad4(id)}`,
    fullName: `${firstName} ${lastName}`.trim(),
    type: 'CONTACT',
    recordStatus: 'ACTIVE',
    entityStatusType: 'ACTIVE',
    origin: body?.origin ?? 'MANUAL',
    organisationId: MOCK_ORG_ID,
    entityTags: body?.entityTags ?? [],
    companyEntities: body?.companyEntities ?? [],
    entitySegments: [],
    createdAt: iso(NOW),
    modifiedAt: iso(NOW),
  };
}

export const isTakenNumber = (value: unknown) =>
  !!value && MOCK_CONTACTS.some((contact) => contact.primaryContactNumber === String(value).replace(/\s+/g, ''));

/** CSV import validation: the checks the backend runs per row. */
export function validateContactsBatch(body: any) {
  const contacts: any[] = Array.isArray(body?.contacts) ? body.contacts : [];
  const seen = new Set<string>();
  const lines = contacts.map((contact) => {
    const violations: { propertyName: string; message: string; invalidValue: string; errorCode: string; constraintDescription: string }[] = [];
    const violation = (propertyName: string, message: string, errorCode: string) =>
      violations.push({
        propertyName,
        message,
        invalidValue: String(contact?.[propertyName] ?? ''),
        errorCode,
        constraintDescription: message,
      });
    const number = String(contact?.primaryContactNumber ?? '').replace(/\s+/g, '');

    if (!String(contact?.firstName ?? '').trim()) {
      violation('firstName', 'First name is required.', 'NotBlank');
    }
    if (!/^\+?\d{8,15}$/.test(number)) {
      violation('primaryContactNumber', 'Phone number must be 8 to 15 digits, with an optional leading +.', 'Pattern');
    } else if (isTakenNumber(number)) {
      violation('primaryContactNumber', 'A contact with this phone number already exists.', 'duplicatePrimaryContactNumber');
    } else if (seen.has(number)) {
      violation('primaryContactNumber', 'This phone number appears more than once in the file.', 'duplicateInBatch');
    }
    if (contact?.emailAddress && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(contact.emailAddress))) {
      violation('emailAddress', 'Email address is not valid.', 'Email');
    }
    seen.add(number);

    return {
      personEntity: { ...contact },
      batchLineValidationStatus: violations.length ? 'PROCESSING_ERROR' : 'PROCESSED_OK',
      constraintViolations: violations,
    };
  });

  return {
    batchValidationStatus: lines.some((line) => line.batchLineValidationStatus !== 'PROCESSED_OK')
      ? 'PROCESSING_ERROR'
      : 'PROCESSED_OK',
    contactsBatchLines: lines,
  };
}

/** CSV import: lines that still pass are saved; numbers already on file are dropped. */
export function importContactsBatch(body: any) {
  const lines: any[] = Array.isArray(body?.contactsBatchLines) ? body.contactsBatchLines : [];
  const successfulBatchLines: any[] = [];
  const failedBatchLines: any[] = [];
  lines.forEach((line, index) => {
    const entity = line?.personEntity ?? {};
    const ok = line?.batchLineValidationStatus !== 'PROCESSING_ERROR' && !isTakenNumber(entity.primaryContactNumber);
    if (ok) {
      successfulBatchLines.push({ ...line, personEntity: newContact(entity, index), batchLineValidationStatus: 'PROCESSED_OK' });
    } else {
      failedBatchLines.push({
        ...line,
        batchLineValidationStatus: 'PROCESSING_ERROR',
        constraintViolations: line?.constraintViolations?.length
          ? line.constraintViolations
          : [{
              propertyName: 'primaryContactNumber',
              message: 'A contact with this phone number already exists.',
              invalidValue: String(entity.primaryContactNumber ?? ''),
              errorCode: 'duplicatePrimaryContactNumber',
              constraintDescription: 'A contact with this phone number already exists.',
            }],
      });
    }
  });
  return {
    batchValidationStatus: failedBatchLines.length ? 'PROCESSING_ERROR' : 'PROCESSED_OK',
    successfulBatchLines,
    failedBatchLines,
  };
}

const csvCell = (value: unknown) => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const CONTACT_CSV_HEADERS = [
  'firstName', 'lastName', 'primaryContactNumber', 'emailAddress', 'gender',
  'birthDate', 'city', 'country',
];

export function contactsCsv(template = false): string {
  const rows = template ? [] : MOCK_CONTACTS.map((contact) =>
    CONTACT_CSV_HEADERS.map((field) => csvCell((contact as unknown as Record<string, unknown>)[field])).join(',')
  );
  return [CONTACT_CSV_HEADERS.join(','), ...rows].join('\n') + '\n';
}

// ------------------------------------------------------- audience helpers

export interface MockContactListEntry {
  id: number;
  organizationUuid: string;
  contactUuids: string | null;
  tagUuids: string | null;
  companyUuids: string | null;
  segmentUuids: string | null;
  contactBlackListed: string | null;
  engagementCampaigns: unknown[];
  engagementMessages: unknown[];
  messageEntities: unknown[];
}

let contactListId = 1;
function listEntry(fields: Partial<MockContactListEntry>): MockContactListEntry {
  return {
    id: contactListId++,
    organizationUuid: MOCK_ORG_ID,
    contactUuids: null,
    tagUuids: null,
    companyUuids: null,
    segmentUuids: null,
    contactBlackListed: null,
    engagementCampaigns: [],
    engagementMessages: [],
    messageEntities: [],
    ...fields,
  };
}

export interface AudienceLists {
  contacts: string[];
  companies: string[];
  tags: string[];
  segments: string[];
  blacklist: string[];
}

const splitList = (value: unknown) =>
  (Array.isArray(value) ? value.map(String) : String(value ?? '').split(','))
    .map((item) => item.trim())
    .filter(Boolean);

/** Recipients as saved on a message or campaign (`engagementContactLists`). */
export function listsOf(entries: MockContactListEntry[] | undefined | null): AudienceLists {
  const lists: AudienceLists = { contacts: [], companies: [], tags: [], segments: [], blacklist: [] };
  for (const entry of entries ?? []) {
    lists.contacts.push(...splitList(entry.contactUuids));
    lists.companies.push(...splitList(entry.companyUuids));
    lists.tags.push(...splitList(entry.tagUuids));
    lists.segments.push(...splitList(entry.segmentUuids));
    lists.blacklist.push(...splitList(entry.contactBlackListed));
  }
  return lists;
}

/** Recipients as a compose payload sends them (ids or uuids, any naming). */
export function listsFromPayload(body: any): AudienceLists & { phoneNumbers: string[] } {
  const pickList = (...keys: string[]) => keys.flatMap((key) => splitList(body?.[key]));
  return {
    contacts: pickList('contactIds', 'contactUuids', 'contacts'),
    companies: pickList('organizationIds', 'companyIds', 'companyUuids', 'organisationIds'),
    tags: pickList('tagIds', 'tagUuids'),
    segments: pickList('segmentIds', 'segmentUuids'),
    blacklist: pickList('blackListedUuids', 'contactBlackListed'),
    phoneNumbers: pickList('phoneNumbers'),
  };
}

export interface AudienceMember {
  contact: MockContact;
  groupType: string;
  groupUuid: string | null;
  groupName: string | null;
}

/** Saved contacts a send reaches, each once, credited to the first group that holds them. */
export function audienceOf(lists: AudienceLists): AudienceMember[] {
  const seen = new Set<string>();
  const audience: AudienceMember[] = [];
  const add = (contact: MockContact, groupType: string, group: MockRef | null) => {
    if (seen.has(contact.uuid)) return;
    seen.add(contact.uuid);
    audience.push({ contact, groupType, groupUuid: group?.uuid ?? null, groupName: group?.name ?? null });
  };
  const groups: [GroupKind, string, string[]][] = [
    ['companies', 'COMPANY', lists.companies],
    ['tags', 'TAG', lists.tags],
    ['segments', 'SEGMENT', lists.segments],
  ];
  for (const [kind, groupType, keys] of groups) {
    for (const key of keys) {
      const group = findGroup(kind, key);
      if (group) groupMembers(kind, key).forEach((contact) => add(contact, groupType, group));
    }
  }
  for (const key of lists.contacts) {
    const contact = findContact(key);
    if (contact) add(contact, 'CONTACT', null);
  }
  return audience;
}

const recipientCard = (contact: MockContact) => ({
  uuid: contact.uuid,
  fullName: contact.fullName,
  gender: contact.gender,
  country: contact.country,
  mobileNumber: contact.primaryContactNumber,
  email: contact.emailAddress,
});

const groupCard = (group: { id: number; uuid: string; name: string; description: string }) => ({
  uuid: group.uuid,
  name: group.name,
  groupId: group.id,
  description: group.description,
});

/** CampaignReceipientsData: who a message or campaign is addressed to. */
function recipientsData(lists: AudienceLists, total: number) {
  const pickGroups = <T>(keys: string[], find: (key: string) => T | undefined) =>
    keys.map(find).filter((group): group is T => !!group);
  return {
    totalReceipients: total,
    contacts: pickGroups(lists.contacts, findContact).map(recipientCard),
    engagementRecipientTags: pickGroups(lists.tags, findTag).map(groupCard),
    engagementRecipientCompanies: pickGroups(lists.companies, findCompany).map(groupCard),
    engagementRecipientSegments: pickGroups(lists.segments, findSegment).map(groupCard),
    blackListedUuids: [...lists.blacklist],
  };
}

/** Number lists uploaded ahead of a large send (recipient-batches), by batch id. */
const RECIPIENT_BATCHES = new Map<string, number>();

/** Deduplicated audience size for a compose payload. */
export function previewRecipientCount(body: any): number {
  const lists = listsFromPayload(body);
  const audience = audienceOf(lists);
  const saved = new Set(audience.map((member) => member.contact.primaryContactNumber));
  const numbers = new Set(lists.phoneNumbers.map((value) => value.replace(/\s+/g, '')).filter((value) => !saved.has(value)));
  return audience.length + numbers.size + (RECIPIENT_BATCHES.get(String(body?.recipientBatchId ?? '')) ?? 0);
}

/** The audience breakdown for a compose payload (POST engagement-messages/preview). */
export function previewRecipients(body: any) {
  const lists = listsFromPayload(body);
  return recipientsData(lists, previewRecipientCount(body));
}

// ---------------------------------------------------------------- messages

export interface MockSchedule {
  id: number;
  uuid: string;
  scheduleDate: string;
  scheduleTime: string;
  scheduleTimeZone: string;
  status: string;
  engagementMessages: unknown[];
}

export interface MockMessage {
  id: number;
  uuid: string;
  callbackData: string;
  flash: boolean;
  from: string;
  senderId?: string;
  destination: string | null;
  destinationNetworkId: string | null;
  intermediateReport: boolean;
  notifyContentType: string;
  notifyUrl: string;
  sendAt: string;
  createdAt: string;
  modifiedAt: string;
  requestId: string;
  content: string;
  messageSubject: string;
  messageTemplate: string;
  transliteration: string;
  validityPeriod: string;
  entityId: string | null;
  referenceEntityId: string;
  applicationId: string;
  languageCode: string;
  countryCode: string;
  deliveryStatus: string | null;
  status: string;
  messageType: string;
  scheduleDate: string | null;
  scheduleTime: string | null;
  scheduleTimeZone: string | null;
  organisationId: string;
  archive: boolean;
  name: string;
  hasAttribute?: boolean;
  attributes?: string[];
  createdBy: MockUserRef;
  modifiedBy: MockUserRef;
  engagementContactLists: MockContactListEntry[];
  engagementMessageSchedules: MockSchedule[];
  engagementCampaigns: unknown[];
  engagementSegments: unknown[];
  engagementOrganisationProfiles: unknown[];
  engagementBroadcastTags: unknown[];
  messageHashTags: unknown[];
  engagementBulk: null;
  deliveryTimeWindow: null;
  engagementContextInformation: null;
}

function blankMessage(id: number): MockMessage {
  return {
    id,
    uuid: `message-${pad4(id)}`,
    callbackData: '',
    flash: false,
    from: '',
    destination: null,
    destinationNetworkId: '65201',
    intermediateReport: true,
    notifyContentType: 'application/json',
    notifyUrl: '',
    sendAt: iso(NOW),
    createdAt: iso(NOW),
    modifiedAt: iso(NOW),
    requestId: `req-${20000 + id}`,
    content: '',
    messageSubject: '',
    messageTemplate: '',
    transliteration: 'NON_UNICODE',
    validityPeriod: '48',
    entityId: null,
    referenceEntityId: '',
    applicationId: 'blackkonnect-cxp',
    languageCode: 'en',
    countryCode: 'BW',
    deliveryStatus: null,
    status: 'Completed',
    messageType: 'INSTANT',
    scheduleDate: null,
    scheduleTime: null,
    scheduleTimeZone: null,
    organisationId: MOCK_ORG_ID,
    archive: false,
    name: `Broadcast #${id}`,
    createdBy: ME,
    modifiedBy: ME,
    engagementContactLists: [],
    engagementMessageSchedules: [],
    engagementCampaigns: [],
    engagementSegments: [],
    engagementOrganisationProfiles: [],
    engagementBroadcastTags: [],
    messageHashTags: [],
    engagementBulk: null,
    deliveryTimeWindow: null,
    engagementContextInformation: null,
  };
}

const placeholders = (content: string) =>
  [...new Set([...content.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((match) => match[1]))];

// Real broadcast copy varies in length and encoding, which decides how many SMS
// parts a send costs: one runs past 160 characters, one carries an em dash (not
// GSM-7, so the whole message goes out as UCS-2) and one is personalised.
const MESSAGE_BODIES = [
  'Your free SOKA trivia question is waiting. Answer 10 in a row to enter Friday\'s P5,000 draw.\nSOKA',
  'The Zebras play Uganda on 15 Nov. Predict the score, answer three questions and you could watch from the VIP stand. Entries close Thursday at 18:00.\nZEBRAS',
  'Trivia is back up — every player who lost a streak today keeps it. Pick up where you left off.\nSOKA',
  'Dumela {{firstName}}, you have 3 free questions left this week. Answer before Sunday midnight to stay in the Top 50.\nPLAY',
  'This week\'s Top 50 is in. Players ranked 1 to 10 share P10,000 and everyone else in the Top 50 wins airtime. Keep playing to climb before Friday\'s draw at 20:00. Reply STOP to opt out.\nSOKA',
  'Payday draw tonight at 20:00. Every correct answer today is one more entry.\nSOKA',
  'Club codes are live. Enter ROLLERS to back Township Rollers and double your entries this week.\nROLLERS',
  'Your account is ready. Dial *123# any time to check your entries and winnings.',
];

const MESSAGE_SUBJECTS = [
  'NEVER PLAYED SOKA 2', 'Zebras v Uganda', 'TRIVIA DOWN', 'Weekly reminder',
  'Top 50 results', 'Payday draw', 'Club codes: Rollers', 'Welcome',
  'NEVER PLAYED SOKA', 'Active Players',
];

const SENDERS = ['16000', 'SOKA', '16000', 'BLACK'];

const MESSAGE_STATUSES = [
  'Completed', 'Completed', 'Pending', 'Completed', 'Active', 'Completed',
  'Draft', 'Completed', 'Pending', 'Canceled',
];

/** Who each broadcast went to; every one includes at least one group. */
function messageContactLists(i: number): MockContactListEntry[] {
  const contacts = i % 2 === 0 ? [MOCK_CONTACTS[(i * 3) % 47].uuid, MOCK_CONTACTS[(i * 3 + 11) % 47].uuid] : [];
  const tags = i % 3 !== 2 ? [MOCK_TAGS[i % MOCK_TAGS.length].uuid] : [];
  const companies = i % 4 === 0 || i % 3 === 2 ? [MOCK_COMPANIES[(i * 5) % MOCK_COMPANIES.length].uuid] : [];
  const segments = i % 5 === 1 ? [MOCK_SEGMENTS[i % MOCK_SEGMENTS.length].uuid] : [];
  return [
    ...contacts.map((uuid) => listEntry({ contactUuids: uuid })),
    ...tags.map((uuid) => listEntry({ tagUuids: uuid })),
    ...companies.map((uuid) => listEntry({ companyUuids: uuid })),
    ...segments.map((uuid) => listEntry({ segmentUuids: uuid })),
  ];
}

const messageRand = stream(1501);

const BROADCASTS: MockMessage[] = Array.from({ length: 34 }, (_, i) => {
  // Spread across the last three days at minute granularity.
  const sentMinutesAgo = messageRand.int(4, 4320);
  const status = MESSAGE_STATUSES[i % MESSAGE_STATUSES.length];
  // Pending sends sit in the queue: scheduled between 40 min and 2 days out.
  const pending = status === 'Pending';
  const sendAt = pending ? agoMinutes(-messageRand.int(40, 2880)) : agoMinutes(sentMinutesAgo);
  const scheduled = pending || i % 4 === 1;
  const author = TEAM[i % TEAM.length];
  const content = MESSAGE_BODIES[i % MESSAGE_BODIES.length];
  const fields = placeholders(content);

  return {
    ...blankMessage(i + 1),
    from: SENDERS[i % SENDERS.length],
    destination: phone(i + 1),
    messageType: scheduled ? 'SCHEDULE' : 'INSTANT',
    ...(scheduled ? scheduleParts(sendAt) : {}),
    scheduleTimeZone: scheduled ? 'Africa/Gaborone' : null,
    sendAt,
    createdAt: agoMinutes(sentMinutesAgo + messageRand.int(1, 90)),
    modifiedAt: agoMinutes(messageRand.int(1, sentMinutesAgo)),
    content,
    // Operators name each send in the subject; it is the field people scan.
    messageSubject: MESSAGE_SUBJECTS[i % MESSAGE_SUBJECTS.length],
    hasAttribute: fields.length > 0,
    attributes: fields,
    entityId: `contact-${pad4((i % 47) + 1)}`,
    status,
    // The gateway's reply when the send was submitted, not a delivery receipt.
    deliveryStatus: status === 'Completed' || status === 'Active' ? 'ACCEPTED' : pending ? 'SCHEDULED' : null,
    archive: (status === 'Completed' && i % 7 === 3) || i === 29,
    createdBy: author,
    modifiedBy: author,
    engagementContactLists: messageContactLists(i),
  };
});

/**
 * A smart broadcast leaves one INSTANT message per CSV row (subject and sender
 * from the form, personalised copy, the gateway's first reply as
 * deliveryStatus) and writes no receipt rows, so these show no delivery data.
 */
const SMART_ROWS: MockMessage[] = (
  [
    ['Kagiso', 12, 'ACCEPTED'],
    ['Naledi', 4, 'ACCEPTED'],
    ['Tebogo', 9, 'QUEUED'],
    ['Boitumelo', 15, 'ACCEPTED'],
    ['Mpho', 2, 'UNKNOWN'],
    ['Lesego', 7, 'ACCEPTED'],
  ] as [string, number, string][]
).map(([firstName, entries, deliveryStatus], n) => {
  const at = iso(NOW - 2950 * MINUTE + n * 2 * SECOND);
  return {
    ...blankMessage(35 + n),
    from: 'SOKA',
    destination: phone(700 + n),
    content: `Dumela ${firstName}, you have ${entries} entries for tonight's P5,000 payday draw. Keep playing to add more.\nSOKA`,
    messageSubject: 'Payday draw: personalised',
    status: 'Completed',
    deliveryStatus,
    sendAt: at,
    createdAt: at,
    modifiedAt: at,
    name: `Smart broadcast row ${n + 1}`,
  };
});

const SMART_UUIDS = new Set(SMART_ROWS.map((message) => message.uuid));

/** Newest first, matching the API's default createdAt,desc sort. */
export const MOCK_MESSAGES: MockMessage[] = [...BROADCASTS, ...SMART_ROWS].sort((a, b) =>
  b.createdAt.localeCompare(a.createdAt)
);

/** Messages created during the session: listed, but no receipts have arrived yet. */
const CREATED_MESSAGES = new Set<string>();
const PLANNED_RECIPIENTS = new Map<string, number>();

export const findMessage = (key: string) => byKey(MOCK_MESSAGES, key);

/** A broadcast or campaign message by numeric id or uuid (GET /api/engagement-messages/{id}). */
export function findAnyMessage(key: string): MockMessage | undefined {
  return findMessage(key) ?? byKey(CAMPAIGN_MESSAGES, key);
}

/** Save a composed message the way the API echoes it back. */
export function createMessage(body: any): MockMessage {
  const id = Math.max(...MOCK_MESSAGES.map((message) => message.id)) + 1;
  const scheduled = /sched/i.test(String(body?.messageType ?? body?.type ?? ''));
  const recipients = body?.recipients ?? body?.batchContactsPayload ?? {};
  const lists = listsFromPayload(recipients);
  const content = String(body?.content ?? body?.message ?? '');
  const sendAt = scheduled && body?.scheduleDate ? new Date(body.scheduleDate).toISOString() : iso(NOW);
  const message: MockMessage = {
    ...blankMessage(id),
    from: String(body?.from ?? body?.senderId ?? 'BLACK'),
    content,
    messageSubject: String(body?.messageSubject ?? body?.subject ?? ''),
    messageTemplate: String(body?.messageTemplate ?? body?.template ?? ''),
    messageType: scheduled ? 'SCHEDULE' : 'INSTANT',
    status: String(body?.status ?? (scheduled ? 'Pending' : 'Completed')),
    scheduleDate: scheduled ? (body?.scheduleDate ? scheduleParts(sendAt).scheduleDate : null) : null,
    scheduleTime: scheduled ? String(body?.scheduleTime ?? scheduleParts(sendAt).scheduleTime) : null,
    scheduleTimeZone: scheduled ? String(body?.scheduleTimeZone ?? 'Africa/Gaborone') : null,
    sendAt,
    deliveryStatus: scheduled ? 'SCHEDULED' : 'ACCEPTED',
    hasAttribute: placeholders(content).length > 0,
    attributes: placeholders(content),
    engagementContactLists: Array.isArray(body?.engagementContactLists)
      ? body.engagementContactLists.map((entry: Partial<MockContactListEntry>) => listEntry(entry))
      : [
          ...lists.contacts.map((uuid) => listEntry({ contactUuids: uuid })),
          ...lists.tags.map((uuid) => listEntry({ tagUuids: uuid })),
          ...lists.companies.map((uuid) => listEntry({ companyUuids: uuid })),
          ...lists.segments.map((uuid) => listEntry({ segmentUuids: uuid })),
        ],
  };
  MOCK_MESSAGES.unshift(message);
  CREATED_MESSAGES.add(message.uuid);
  PLANNED_RECIPIENTS.set(message.uuid, previewRecipientCount(recipients) || audienceOf(listsOf(message.engagementContactLists)).length);
  return message;
}

/** PATCH: archive, unarchive, cancel. */
export function updateMessage(key: string, body: any): MockMessage | undefined {
  const message = findMessage(key);
  if (!message) return undefined;
  if (typeof body?.archive === 'boolean') message.archive = body.archive;
  if (typeof body?.status === 'string' && body.status) message.status = body.status;
  if (typeof body?.messageSubject === 'string') message.messageSubject = body.messageSubject;
  if (typeof body?.content === 'string') message.content = body.content;
  message.modifiedAt = iso(Date.now());
  return message;
}

export function deleteMessage(key: string): boolean {
  const index = MOCK_MESSAGES.findIndex((message) => message.uuid === key || String(message.id) === key);
  if (index < 0) return false;
  MOCK_MESSAGES.splice(index, 1);
  return true;
}

/** Message list and count criteria; newest first unless sorted. */
export function messageQuery(params: URLSearchParams): MockMessage[] {
  return query(MOCK_MESSAGES, params, { sort: 'createdAt,desc' });
}

// --------------------------------------------------------------- campaigns

interface CampaignPlan {
  name: string;
  status: string;
  objective: string;
  /** Days ago the campaign was created, started (negative = future) and how long it runs. */
  created: number;
  start: number;
  run: number;
  companies?: number[];
  tags?: number[];
  segments?: number[];
  contacts?: number[];
  /** Positions in the audience to put on the do-not-contact list. */
  blacklist?: number[];
  sender?: string;
  copy: [subject: string, content: string][];
}

const CAMPAIGN_PLAN: CampaignPlan[] = [
  {
    name: 'Back to School Data Bundles', status: 'ACTIVE', objective: 'Acquisition',
    created: 21, start: 14, run: 30, tags: [6], segments: [0], blacklist: [1],
    copy: [
      ['Back to school bundle', 'Back to school: 5GB for P50, valid 30 days. Dial *150*5# to buy before the end of the month.\nBLACK'],
      ['Bundle reminder', 'Dumela {{firstName}}, the school bundle offer ends Friday. Dial *150*5# for 5GB at P50.\nBLACK'],
    ],
  },
  {
    name: 'Festive Season Airtime Promo', status: 'COMPLETED', objective: 'Awareness',
    created: 70, start: 60, run: 21, tags: [0, 1], companies: [0],
    copy: [
      ['Festive airtime bonus', 'Top up P20 or more before 26 December and get 50% bonus airtime. Reply STOP to opt out.\nBLACK'],
      ['Last day for bonus', 'Last day: top up P20 or more today and get 50% bonus airtime.\nBLACK'],
    ],
  },
  {
    name: 'Winter Retention Push', status: 'ACTIVE', objective: 'Retention',
    created: 12, start: 7, run: 28, segments: [2], tags: [3], blacklist: [0, 3],
    copy: [
      ['Winter loyalty offer', 'Thank you for staying with us, {{firstName}}. Enjoy 1GB free this week. Dial *150*7# to claim it.\nBLACK'],
    ],
  },
  {
    name: 'New Tariff Announcement', status: 'DISABLED', objective: 'Awareness',
    created: 40, start: 33, run: 14, tags: [7],
    copy: [
      ['New tariff notice', 'From 1 March, calls to all local networks cost 75t a minute. Full details at blackkonnect.io/tariffs\nBLACK'],
    ],
  },
  {
    name: 'Customer Satisfaction Survey', status: 'SCHEDULED', objective: 'Feedback',
    created: 3, start: -5, run: 10, companies: [2, 4], contacts: [1, 9],
    copy: [
      ['How did we do?', 'How likely are you to recommend us to a friend? Reply with a number from 0 to 10. Replies are free.\nBLACK'],
      ['Survey reminder', 'Reminder: reply 0 to 10 to tell us how we did. Replies are free.\nBLACK'],
    ],
  },
  {
    name: 'Dormant Subscriber Winback', status: 'COMPLETED', objective: 'Re-engagement',
    created: 120, start: 110, run: 30, segments: [4], tags: [3],
    copy: [
      ['We miss you', 'We miss you, {{firstName}}. Recharge P10 this week and get 500MB free.\nBLACK'],
      ['Final winback offer', 'Final chance: recharge P10 by Sunday for 500MB free.\nBLACK'],
    ],
  },
  {
    name: 'Okavango Roaming Offer', status: 'IN_PROGRESS', objective: 'Upsell',
    created: 9, start: 2, run: 12, segments: [3], tags: [5],
    copy: [
      ['Roaming in Namibia', 'Travelling to Namibia? Roam at local rates for P35 a day. Dial *150*9# to switch it on.\nBLACK'],
    ],
  },
  {
    name: 'Prepaid Top-up Reminder', status: 'PAUSED', objective: 'Retention',
    created: 30, start: 20, run: 40, tags: [6],
    copy: [
      ['Low balance reminder', 'Your balance is running low. Top up at any Choppies till or dial *104# to buy airtime.\nBLACK'],
    ],
  },
  {
    name: 'Independence Day Flash Sale', status: 'FAILED', objective: 'Acquisition',
    created: 18, start: 16, run: 3, tags: [0], companies: [1],
    copy: [
      ['Independence Day sale', 'Happy Independence Day. Today only: double data on every bundle — dial *150# to buy.\nBLACK'],
    ],
  },
  {
    name: 'Loyalty Points Expiry Notice', status: 'DRAFTED', objective: 'Retention',
    created: 1, start: -10, run: 14, tags: [2],
    copy: [
      ['Points expiring', 'Dumela {{firstName}}, 1,200 loyalty points expire at the end of the month. Dial *150*3# to spend them.\nBLACK'],
    ],
  },
  {
    name: 'Zebras Match Day Alerts', status: 'CANCELED', objective: 'Awareness',
    created: 25, start: 15, run: 10, tags: [4], segments: [1], sender: 'ZEBRAS',
    copy: [
      ['Match day', 'Zebras v Uganda kicks off at 15:00 at the National Stadium. Follow live scores on *123#.\nZEBRAS'],
    ],
  },
  {
    name: 'Year-end Thank You', status: 'FINISHED', objective: 'Retention',
    created: 300, start: 290, run: 7, tags: [0], segments: [0],
    copy: [
      ['Thank you', 'Thank you for a great year. Here is 1GB on us, valid until 7 January.\nBLACK'],
    ],
  },
];

/** Schedule state from the campaign's state and whether the slot has passed. */
function scheduleStatusFor(campaignStatus: string, whenMs: number): string {
  const past = whenMs <= NOW;
  switch (campaignStatus) {
    case 'DRAFTED':
      return 'PENDING';
    case 'FAILED':
      return past ? 'FAILED' : 'CANCELED';
    case 'DISABLED':
    case 'CANCELED':
      return past ? 'COMPLETED' : 'CANCELED';
    case 'PAUSED':
      return past ? 'COMPLETED' : 'PAUSED';
    default:
      return past ? 'COMPLETED' : 'PENDING';
  }
}

function messageStatusFor(schedules: MockSchedule[]): string {
  const states = schedules.map((schedule) => schedule.status);
  if (states.includes('STARTED')) return 'Active';
  if (states.some((state) => state === 'PENDING' || state === 'PAUSED')) return 'Pending';
  if (states.every((state) => state === 'CANCELED')) return 'Canceled';
  return 'Completed';
}

const SLOTS: [number, number][] = [[9, 0], [13, 0], [17, 30]];

let campaignMessageId = 2001;
let scheduleId = 5001;

function makeSchedule(whenMs: number, status: string): MockSchedule {
  const id = scheduleId++;
  return {
    id,
    uuid: `schedule-${pad4(id)}`,
    ...scheduleParts(whenMs),
    scheduleTimeZone: 'Africa/Gaborone',
    status,
    engagementMessages: [],
  };
}

const scheduleMs = (schedule: MockSchedule) =>
  new Date(`${schedule.scheduleDate}T${schedule.scheduleTime}:00`).getTime();

function campaignMessage(
  plan: CampaignPlan,
  index: number,
  [subject, content]: [string, string],
  schedules: MockSchedule[],
  createdAt: string
): MockMessage {
  const id = campaignMessageId++;
  const sender = plan.sender ?? 'BLACK';
  const first = schedules[0];
  const fields = placeholders(content);
  return {
    ...blankMessage(id),
    uuid: `campaign-message-${pad4(id)}`,
    name: `${plan.name}: message ${index + 1}`,
    from: sender,
    senderId: sender,
    content,
    messageSubject: subject,
    messageType: 'SCHEDULE',
    status: messageStatusFor(schedules),
    scheduleDate: first?.scheduleDate ?? null,
    scheduleTime: first?.scheduleTime ?? null,
    scheduleTimeZone: 'Africa/Gaborone',
    sendAt: first ? iso(scheduleMs(first)) : createdAt,
    createdAt,
    modifiedAt: createdAt,
    hasAttribute: fields.length > 0,
    attributes: fields,
    engagementMessageSchedules: schedules,
  };
}

const csv = (values: string[]) => (values.length ? values.join(',') : null);

const CAMPAIGN_MESSAGES: MockMessage[] = [];

const campaignRand = stream(1601);

const CAMPAIGNS_BY_PLAN = CAMPAIGN_PLAN.map((plan, i) => {
  const startMs = startOfDay(NOW - plan.start * DAY);
  const endMs = startMs + plan.run * DAY;
  const createdAt = ago(plan.created);

  const messages = plan.copy.map((copy, m) => {
    let schedules: MockSchedule[];
    if (plan.status === 'IN_PROGRESS' && m === 0) {
      // One slot is going out right now, so the campaign has receipts in flight.
      schedules = [
        makeSchedule(startMs + 9 * HOUR, 'COMPLETED'),
        makeSchedule(NOW - 40 * MINUTE, 'STARTED'),
        makeSchedule(startOfDay(NOW + 2 * DAY) + 9 * HOUR, 'PENDING'),
      ];
    } else {
      const count = 1 + ((i + m) % 3);
      // Slots spread across the run, so live campaigns still have sends ahead.
      const gap = Math.max(1, Math.floor(plan.run / count));
      schedules = Array.from({ length: count }, (_, k) => {
        const day = Math.min(plan.run, m * 2 + k * gap);
        const [hour, minute] = SLOTS[(m + k + i) % SLOTS.length];
        const when = startMs + day * DAY + hour * HOUR + minute * MINUTE;
        return makeSchedule(when, scheduleStatusFor(plan.status, when));
      });
    }
    const message = campaignMessage(plan, m, copy, schedules, createdAt);
    CAMPAIGN_MESSAGES.push(message);
    return message;
  });

  const lists: AudienceLists = {
    companies: (plan.companies ?? []).map((k) => MOCK_COMPANIES[k].uuid),
    tags: (plan.tags ?? []).map((t) => MOCK_TAGS[t].uuid),
    segments: (plan.segments ?? []).map((s) => MOCK_SEGMENTS[s].uuid),
    contacts: (plan.contacts ?? []).map((c) => MOCK_CONTACTS[c].uuid),
    blacklist: [],
  };
  const audience = audienceOf(lists);
  lists.blacklist = (plan.blacklist ?? [])
    .map((position) => audience[position]?.contact.uuid)
    .filter((uuid): uuid is string => !!uuid);

  const started = startMs <= NOW && plan.status !== 'DRAFTED' && plan.status !== 'SCHEDULED';

  return {
    id: i + 1,
    uuid: `campaign-${pad4(i + 1)}`,
    name: plan.name,
    description: `${plan.objective} campaign: ${plan.copy[0][0].toLowerCase()} to selected subscribers`,
    startDate: iso(startMs),
    endDate: iso(endMs),
    creatorUserId: MOCK_USER_ID,
    starterUserId: started ? MOCK_USER_ID : null,
    communicationType: 'SMS',
    campaignType: 'BROADCAST',
    requestTypeId: 1,
    status: plan.status,
    scheduledAt: iso(startMs),
    startedAt: started ? iso(startMs + 9 * HOUR) : null,
    launchedAt: started ? iso(startMs + 9 * HOUR) : null,
    bulkJobId: started ? `job-${1000 + i}` : null,
    usingPeople: true,
    hasDoNotContactList: lists.blacklist.length > 0,
    hasRealtimeAudience: i % 3 === 0,
    archived: false,
    stopped: plan.status === 'DISABLED' || plan.status === 'CANCELED',
    createdAt,
    modifiedAt: ago(Math.min(plan.created, campaignRand.int(0, 6))),
    organisationId: MOCK_ORG_ID,
    broadcast: null,
    createdBy: ME,
    modifiedBy: ME,
    engagementObjectives: [{ id: i + 1, name: plan.objective, engagementCampaigns: [] as unknown[] }],
    engagementOrganizations: lists.companies.map((uuid, k) => {
      const company = findCompany(uuid)!;
      return { id: i * 10 + k + 1, organizationId: uuid, organizationIntId: company.id, name: company.name, engagementCampaigns: [], engagementContacts: [] };
    }),
    engagementRecipientTags: lists.tags.map((uuid, k) => {
      const tag = findTag(uuid)!;
      return { id: i * 10 + k + 1, tagUuid: uuid, tagIntId: tag.id, name: tag.name, engagementCampaigns: [], engagementContacts: [] };
    }),
    engagementRecipientSegments: lists.segments.map((uuid, k) => {
      const segment = findSegment(uuid)!;
      return { id: i * 10 + k + 1, segmentUuid: uuid, segmentIntId: segment.id, name: segment.name, engagementCampaigns: [], engagementContacts: [] };
    }),
    engagementMessages: messages,
    engagementHashTags: [] as unknown[],
    engagementSchedules: [{ id: i + 1, startDate: iso(startMs), endDate: iso(endMs), engagementCampaigns: [] as unknown[] }],
    engagementContactLists: [
      listEntry({
        contactUuids: csv(lists.contacts),
        tagUuids: csv(lists.tags),
        companyUuids: csv(lists.companies) ?? '',
        segmentUuids: csv(lists.segments) ?? '',
        contactBlackListed: csv(lists.blacklist),
      }),
    ],
  };
});

export type MockCampaign = (typeof CAMPAIGNS_BY_PLAN)[number];

/** Newest first, as the list and the dashboard summary show them. */
export const MOCK_CAMPAIGNS: MockCampaign[] = [...CAMPAIGNS_BY_PLAN].sort((a, b) =>
  b.createdAt.localeCompare(a.createdAt)
);

export const findCampaign = (key: string) => byKey(MOCK_CAMPAIGNS, key);

/** Give new messages and schedules in a campaign payload ids, as the API does on save. */
function normaliseCampaignMessages(messages: any[], campaign: { createdAt: string; name: string }): MockMessage[] {
  const knownSchedules = CAMPAIGN_MESSAGES.flatMap((message) => message.engagementMessageSchedules);
  return (Array.isArray(messages) ? messages : []).map((raw: any, index: number) => {
    const existing = raw?.id != null ? byKey(CAMPAIGN_MESSAGES, String(raw.id)) : undefined;
    const schedules: MockSchedule[] = (Array.isArray(raw?.engagementMessageSchedules) ? raw.engagementMessageSchedules : []).map(
      (schedule: any): MockSchedule => {
        const known = schedule?.id != null ? knownSchedules.find((candidate) => candidate.id === Number(schedule.id)) : undefined;
        if (known) {
          return {
            ...known,
            scheduleDate: String(schedule.scheduleDate ?? known.scheduleDate).slice(0, 10),
            scheduleTime: String(schedule.scheduleTime ?? known.scheduleTime),
            scheduleTimeZone: String(schedule.scheduleTimeZone ?? known.scheduleTimeZone),
            engagementMessages: [],
          };
        }
        const id = scheduleId++;
        return {
          id,
          uuid: `schedule-${pad4(id)}`,
          scheduleDate: String(schedule?.scheduleDate ?? '').slice(0, 10),
          scheduleTime: String(schedule?.scheduleTime ?? '09:00'),
          scheduleTimeZone: String(schedule?.scheduleTimeZone ?? 'Africa/Gaborone'),
          status: 'PENDING',
          engagementMessages: [],
        };
      }
    );
    if (existing) {
      Object.assign(existing, {
        messageSubject: raw.messageSubject ?? existing.messageSubject,
        content: raw.content ?? existing.content,
        from: raw.senderId ?? raw.from ?? existing.from,
        senderId: raw.senderId ?? raw.from ?? existing.senderId,
        engagementMessageSchedules: schedules,
        status: messageStatusFor(schedules),
      });
      return existing;
    }
    const plan: CampaignPlan = { name: campaign.name, status: 'ACTIVE', objective: '', created: 0, start: 0, run: 0, copy: [], sender: raw?.senderId ?? raw?.from };
    const message = campaignMessage(plan, index, [String(raw?.messageSubject ?? raw?.subject ?? ''), String(raw?.content ?? raw?.body ?? '')], schedules, iso(Date.now()));
    CAMPAIGN_MESSAGES.push(message);
    return message;
  });
}

/** PUT: the portal sends the whole campaign back with its edits. */
export function updateCampaign(key: string, body: any): MockCampaign | undefined {
  const campaign = findCampaign(key);
  if (!campaign) return undefined;
  const scalar = ['name', 'description', 'startDate', 'endDate', 'status', 'archived', 'stopped'] as const;
  for (const field of scalar) {
    if (body?.[field] !== undefined) (campaign as unknown as Record<string, unknown>)[field] = body[field];
  }
  if (Array.isArray(body?.engagementObjectives)) campaign.engagementObjectives = body.engagementObjectives;
  if (Array.isArray(body?.engagementMessages)) {
    campaign.engagementMessages = normaliseCampaignMessages(body.engagementMessages, campaign);
  }
  if (Array.isArray(body?.engagementContactLists) && body.engagementContactLists.length) {
    // Saved lists keep their ids; new ones get one.
    campaign.engagementContactLists = body.engagementContactLists.map(
      (entry: Partial<MockContactListEntry>): MockContactListEntry => ({ ...listEntry({}), ...entry })
    );
  }
  campaign.modifiedAt = iso(Date.now());
  return campaign;
}

/** POST: a new campaign from the create wizard. */
export function createCampaign(body: any): MockCampaign {
  const id = Math.max(...MOCK_CAMPAIGNS.map((campaign) => campaign.id)) + 1;
  const now = iso(Date.now());
  const payload = body?.batchContactsPayload ?? body?.recipients ?? {};
  const lists = listsFromPayload(payload);
  const base = CAMPAIGNS_BY_PLAN[0];
  const campaign: MockCampaign = {
    ...base,
    id,
    uuid: `campaign-${pad4(id)}`,
    name: String(body?.name ?? 'Untitled campaign'),
    description: String(body?.description ?? ''),
    startDate: body?.startDate ?? now,
    endDate: body?.endDate ?? now,
    status: 'SCHEDULED',
    scheduledAt: body?.startDate ?? now,
    startedAt: null,
    launchedAt: null,
    starterUserId: null,
    bulkJobId: null,
    hasDoNotContactList: false,
    hasRealtimeAudience: false,
    stopped: false,
    createdAt: now,
    modifiedAt: now,
    engagementObjectives: (Array.isArray(body?.engagementObjectives) ? body.engagementObjectives : []).map(
      (objective: any, k: number) => ({ id: id * 10 + k, name: objective?.name ?? String(objective), engagementCampaigns: [] })
    ),
    engagementOrganizations: [],
    engagementRecipientTags: [],
    engagementRecipientSegments: [],
    engagementMessages: [],
    engagementSchedules: [{ id, startDate: body?.startDate ?? now, endDate: body?.endDate ?? now, engagementCampaigns: [] }],
    engagementContactLists: [
      listEntry({
        contactUuids: csv(lists.contacts),
        tagUuids: csv(lists.tags),
        companyUuids: csv(lists.companies) ?? '',
        segmentUuids: csv(lists.segments) ?? '',
      }),
    ],
  };
  campaign.engagementMessages = normaliseCampaignMessages(body?.engagementMessages ?? [], campaign);
  MOCK_CAMPAIGNS.unshift(campaign);
  return campaign;
}

// -------------------------------------------------------- delivery receipts

type Phase = 'sent' | 'sending' | 'failed' | 'unsent';

/**
 * One dispatch: a broadcast, or one schedule slot of a campaign message.
 * Receipt rows are addressed by index and generated on demand.
 */
export interface SendUnit {
  ordinal: number;
  key: string;
  hash: number;
  message: MockMessage;
  campaign: MockCampaign | null;
  schedule: MockSchedule | null;
  phase: Phase;
  sentAtMs: number;
  audience: AudienceMember[];
  blacklist: ReadonlySet<string>;
  /** Receipt rows (0 until the send goes out). */
  rows: number;
  delivered: number;
  failed: number;
  inFlight: number;
  /** Bijection index → slot, so outcomes spread through the list but total exactly. */
  stride: number;
  offset: number;
}

/** Receipt states that mean the SMS will not arrive (DeliveryService's FAILED_STATES). */
export const FAILED_STATES = ['REJECTED', 'UNDELIVERABLE', 'EXPIRED', 'DELETED', 'SKIPPED'];
const FAILED_KINDS = ['UNDELIVERABLE', 'EXPIRED', 'UNDELIVERABLE', 'REJECTED', 'EXPIRED', 'UNDELIVERABLE', 'DELETED', 'REJECTED'];
const HARD_FAILURES = ['REJECTED', 'REJECTED', 'REJECTED', 'UNDELIVERABLE'];
const IN_FLIGHT_KINDS = ['ACCEPTED', 'EN_ROUTE', 'PENDING', 'ACCEPTED', 'QUEUED', 'EN_ROUTE', 'UNKNOWN', 'PENDING'];

/**
 * How a send split. Most land cleanly, some lose a few recipients, the odd
 * one fails outright; sends in progress have receipts in flight, and a few
 * finished ones never heard back from some handsets.
 */
function plannedSplit(hash: number, total: number, phase: Phase) {
  if (phase === 'unsent' || total === 0) return { delivered: 0, failed: 0, inFlight: 0 };
  if (phase === 'failed') return { delivered: 0, failed: total, inFlight: 0 };
  const roll = hash % 12;
  const failShare = roll < 6 ? 0 : roll < 11 ? ((hash % 40) + 3) / 1000 : 1;
  const failed = failShare === 1 ? total : failShare > 0 ? Math.max(1, Math.round(total * failShare)) : 0;
  // Clean sends where a few handsets never sent a receipt back.
  const silent = phase === 'sent' && roll < 6 && (hash >>> 5) % 2 === 0 ? Math.max(1, Math.round(total * 0.012)) : 0;
  const inFlight = failed >= total ? 0 : Math.min(total - failed, phase === 'sending' ? Math.round(total * 0.38) : silent);
  return { delivered: total - failed - inFlight, failed, inFlight };
}

/** Seeds a send's numbers; mixed, because neighbouring uuids hash to neighbouring values. */
const unitHash = (key: string) => mix(hashOf(key), 0x5bd1);

function buildUnit(
  ordinal: number,
  key: string,
  message: MockMessage,
  campaign: MockCampaign | null,
  schedule: MockSchedule | null,
  phase: Phase,
  sentAtMs: number,
  lists: AudienceLists,
  total: number
): SendUnit {
  const hash = unitHash(key);
  const rows = phase === 'unsent' ? 0 : total;
  const split = plannedSplit(hash, rows, phase);
  return {
    ordinal,
    key,
    hash,
    message,
    campaign,
    schedule,
    phase,
    sentAtMs,
    audience: audienceOf(lists),
    blacklist: new Set(lists.blacklist),
    rows,
    ...split,
    stride: rows && rows % 7919 === 0 ? 104729 : 7919,
    offset: rows ? hash % rows : 0,
  };
}

const phaseOfMessage = (status: string): Phase =>
  /^completed$/i.test(status) ? 'sent' : /^active$/i.test(status) ? 'sending' : 'unsent';

const phaseOfSchedule = (status: string): Phase =>
  status === 'COMPLETED' || status === 'FINISHED' ? 'sent'
    : status === 'STARTED' || status === 'PROCESSING' ? 'sending'
    : status === 'FAILED' ? 'failed'
    : 'unsent';

/** Broadcast audience: saved contacts plus phone numbers, 300 to 5,199 in all. */
const broadcastSize = (uuid: string) => 300 + (unitHash(uuid) % 4900);

/**
 * Built once, on first use. Receipts are history: editing a campaign later
 * adds pending slots (no receipts yet) and never rewrites past sends.
 */
let unitCache: SendUnit[] | null = null;
const countCache = new Map<SendUnit, Record<string, number>>();

export function sendUnits(): SendUnit[] {
  if (unitCache) return unitCache;
  const units: SendUnit[] = [];
  for (const message of MOCK_MESSAGES) {
    if (SMART_UUIDS.has(message.uuid) || CREATED_MESSAGES.has(message.uuid)) continue;
    units.push(
      buildUnit(units.length + 1, message.uuid, message, null, null, phaseOfMessage(message.status),
        Date.parse(message.sendAt), listsOf(message.engagementContactLists), broadcastSize(message.uuid))
    );
  }
  for (const campaign of MOCK_CAMPAIGNS) {
    const lists = listsOf(campaign.engagementContactLists);
    const size = audienceOf(lists).length;
    for (const message of campaign.engagementMessages) {
      for (const schedule of message.engagementMessageSchedules) {
        units.push(
          buildUnit(units.length + 1, schedule.uuid, message, campaign, schedule, phaseOfSchedule(schedule.status),
            scheduleMs(schedule), lists, size)
        );
      }
    }
  }
  unitCache = units;
  return units;
}

/** Receipt state of row `i`. */
export function statusAt(unit: SendUnit, i: number): string {
  const member = unit.audience[i];
  if (member && unit.blacklist.has(member.contact.uuid)) return 'SKIPPED';
  const slot = (unit.stride * i + unit.offset) % unit.rows;
  if (slot < unit.delivered) return 'DELIVERED';
  if (slot < unit.delivered + unit.failed) {
    return unit.phase === 'failed'
      ? HARD_FAILURES[(slot + unit.hash) % HARD_FAILURES.length]
      : FAILED_KINDS[(slot * 7 + unit.hash) % FAILED_KINDS.length];
  }
  return IN_FLIGHT_KINDS[(slot * 5 + unit.hash) % IN_FLIGHT_KINDS.length];
}

/** Rows per receipt state for one unit (cached). */
function stateCounts(unit: SendUnit): Record<string, number> {
  const cached = countCache.get(unit);
  if (cached) return cached;
  const counts: Record<string, number> = {};
  for (let i = 0; i < unit.rows; i++) {
    const state = statusAt(unit, i);
    counts[state] = (counts[state] ?? 0) + 1;
  }
  countCache.set(unit, counts);
  return counts;
}

function summarise(units: SendUnit[]) {
  const summary = { total: 0, delivered: 0, failed: 0, inFlight: 0 };
  for (const unit of units) {
    for (const [state, count] of Object.entries(stateCounts(unit))) {
      summary.total += count;
      if (state === 'DELIVERED') summary.delivered += count;
      else if (FAILED_STATES.includes(state)) summary.failed += count;
      else summary.inFlight += count;
    }
  }
  return summary;
}

/**
 * Delivery receipts for a message, schedule or campaign uuid: what
 * DeliveryService.summary() builds from its three count queries.
 */
export function mockDelivery(uuid: string) {
  return summarise(
    sendUnits().filter((unit) => unit.message.uuid === uuid || unit.key === uuid || unit.campaign?.uuid === uuid)
  );
}

/** Who received row `i`: a saved contact first, then subscribers from uploaded number lists. */
function recipientAt(unit: SendUnit, i: number) {
  const member = unit.audience[i];
  if (member) {
    const contact = member.contact;
    return {
      contactUuid: contact.uuid as string | null,
      fullName: contact.fullName,
      mobileNumber: contact.primaryContactNumber,
      gender: contact.gender as string | null,
      age: contact.age as number | null,
      groupType: member.groupType as string | null,
      groupUuid: member.groupUuid,
      group: member.groupName,
    };
  }
  // A shared subscriber base, so the same numbers turn up across sends.
  const subscriber = 20000 + (((unit.hash % 4000) + i - unit.audience.length) % 60000);
  const genderRoll = mix(subscriber, 3);
  return {
    contactUuid: null as string | null,
    fullName: `${FIRST_NAMES[mix(subscriber, 1) % FIRST_NAMES.length]} ${LAST_NAMES[mix(subscriber, 2) % LAST_NAMES.length]}`,
    mobileNumber: phone(subscriber),
    gender: (genderRoll % 25 === 0 ? 'OTHER' : genderRoll % 2 ? 'MALE' : 'FEMALE') as string | null,
    age: (mix(subscriber, 4) % 12 === 0 ? null : 18 + (mix(subscriber, 5) % 52)) as number | null,
    groupType: null as string | null,
    groupUuid: null as string | null,
    group: null as string | null,
  };
}

/** Submissions leave at about 25 a second. */
const rowCreatedMs = (unit: SendUnit, i: number) => unit.sentAtMs + Math.floor(i / 25) * SECOND;

function receiptMs(unit: SendUnit, i: number, state: string): number {
  const created = rowCreatedMs(unit, i);
  const jitter = mix(unit.hash, i);
  let at = created;
  if (state === 'DELIVERED') at = created + (2 + (jitter % 90)) * SECOND;
  else if (state === 'EXPIRED') at = created + 48 * HOUR;
  else if (FAILED_STATES.includes(state)) at = created + (4 + (jitter % 600)) * SECOND;
  return Math.min(at, Date.now());
}

/** EngagementMessageReportsDTO: one recipient's receipt. */
export function reportRow(unit: SendUnit, i: number, state = statusAt(unit, i)) {
  const recipient = recipientAt(unit, i);
  const created = rowCreatedMs(unit, i);
  return {
    id: unit.ordinal * 100000 + i + 1,
    uuid: `${unit.key}-r${i + 1}`,
    messageUuid: unit.message.uuid,
    subject: unit.message.messageSubject,
    content: unit.message.content,
    scheduleUuid: unit.schedule?.uuid ?? null,
    messageSchedules: unit.schedule ? `${unit.schedule.scheduleDate}T${unit.schedule.scheduleTime}:00` : null,
    campaignUuid: unit.campaign?.uuid ?? null,
    contactUuid: recipient.contactUuid,
    mobileNumber: recipient.mobileNumber,
    fullName: recipient.fullName,
    groupUuid: recipient.groupUuid,
    group: recipient.group,
    groupType: recipient.groupType,
    gender: recipient.gender,
    age: recipient.age,
    country: 'Botswana',
    status: null as string | null,
    deliveryStatus: state,
    scheduleStatus: unit.schedule?.status ?? null,
    blackListed: state === 'SKIPPED',
    networkMessageId: state === 'SKIPPED' ? null : `${(unit.hash % 0xffff).toString(16)}${(i + 1).toString(16).padStart(6, '0')}`,
    timeStamp: iso(receiptMs(unit, i, state)),
    createdAt: iso(created),
    organisationUuId: MOCK_ORG_ID,
  };
}

export type MockReportRow = ReturnType<typeof reportRow>;

interface ReceiptHit {
  unit: SendUnit;
  i: number;
  state: string;
  createdMs: number;
}

/** Criteria the receipt engine answers without building rows. */
const UNIT_KEYS = new Set([
  'messageUuid.equals', 'messageUuid.in', 'campaignUuid.equals', 'campaignUuid.in',
  'scheduleUuid.equals', 'scheduleUuid.in', 'contactUuid.equals',
]);
const STATE_KEYS = ['deliveryStatus.equals', 'deliveryStatus.in', 'deliveryStatus.notEquals', 'deliveryStatus.notIn'];
const TIME_OPS = ['greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual'];
const TIME_KEYS = TIME_OPS.map((op) => `createdAt.${op}`);
const HANDLED_KEYS = new Set([...UNIT_KEYS, ...STATE_KEYS, ...TIME_KEYS, 'blackListed.equals']);

function selectUnits(params: URLSearchParams, scope?: string): SendUnit[] {
  const wanted = (field: string) => [...listValues(params, `${field}.equals`), ...listValues(params, `${field}.in`)];
  const messages = wanted('messageUuid');
  const campaigns = wanted('campaignUuid');
  const schedules = wanted('scheduleUuid');
  return sendUnits().filter(
    (unit) =>
      unit.rows > 0 &&
      (!scope || unit.message.uuid === scope || unit.key === scope || unit.campaign?.uuid === scope) &&
      (!messages.length || messages.includes(unit.message.uuid)) &&
      (!campaigns.length || (!!unit.campaign && campaigns.includes(unit.campaign.uuid))) &&
      (!schedules.length || (!!unit.schedule && schedules.includes(unit.schedule.uuid)))
  );
}

/** Every receipt matching the query, as light (unit, index) pairs. */
function receiptHits(params: URLSearchParams, scope?: string, options: { excludeSkipped?: boolean; fromMs?: number; toMs?: number } = {}): ReceiptHit[] {
  const contact = params.get('contactUuid.equals');
  const blackListed = params.get('blackListed.equals');
  const stateKeys = STATE_KEYS.filter((key) => params.has(key));
  const timeKeys = TIME_KEYS.filter((key) => params.has(key));
  const rowLevel = keysOf(params).some((key) => {
    if (PAGING_KEYS.has(key) || HANDLED_KEYS.has(key)) return false;
    const dot = key.lastIndexOf('.');
    return dot > 0 && !TENANT_FIELD.test(key.slice(0, dot));
  });

  const hits: ReceiptHit[] = [];
  for (const unit of selectUnits(params, scope)) {
    let indices: number[] | null = null;
    if (contact) {
      const position = unit.audience.findIndex((member) => member.contact.uuid === contact);
      if (position < 0) continue;
      indices = [position];
    }
    const count = indices ? indices.length : unit.rows;
    for (let n = 0; n < count; n++) {
      const i = indices ? indices[n] : n;
      const state = statusAt(unit, i);
      if (options.excludeSkipped && state === 'SKIPPED') continue;
      if (stateKeys.some((key) => !testCriterion(state, key.split('.')[1], params.get(key) ?? '', params, key))) continue;
      if (blackListed != null && (state === 'SKIPPED') !== (blackListed === 'true')) continue;
      const createdMs = rowCreatedMs(unit, i);
      if (options.fromMs != null && createdMs < options.fromMs) continue;
      if (options.toMs != null && createdMs > options.toMs) continue;
      if (timeKeys.some((key) => !testCriterion(createdMs, key.split('.')[1], params.get(key) ?? '', params, key))) continue;
      if (rowLevel && !matchesCriteria(reportRow(unit, i, state), params, plainGet, HANDLED_KEYS)) continue;
      hits.push({ unit, i, state, createdMs });
    }
  }
  return hits;
}

/** Sort hits; time-based sorts stay cheap, other fields build the rows first. */
function sortHits(hits: ReceiptHit[], params: URLSearchParams): ReceiptHit[] {
  const spec = params.getAll('sort').filter(Boolean)[0];
  if (!spec) return hits;
  const [field, dir] = spec.split(',');
  const desc = (dir ?? '').trim().toLowerCase() === 'desc';
  if (['createdAt', 'timeStamp', 'id', 'createdDate'].includes(field.trim())) {
    return [...hits].sort((a, b) => (a.createdMs - b.createdMs || a.i - b.i) * (desc ? -1 : 1));
  }
  const rows = new Map(hits.map((hit) => [hit, reportRow(hit.unit, hit.i, hit.state)]));
  const sorted = sortList([...rows.values()], params);
  const order = new Map(sorted.map((row, index) => [row, index]));
  return [...hits].sort((a, b) => (order.get(rows.get(a)!) ?? 0) - (order.get(rows.get(b)!) ?? 0));
}

/** One page of receipts (Spring default page size 20) and the total for X-Total-Count. */
function receiptPage(hits: ReceiptHit[], params: URLSearchParams, defaultSize = 20) {
  const page = pageSlice(sortHits(hits, params), params, defaultSize);
  return { rows: page.map((hit) => reportRow(hit.unit, hit.i, hit.state)), total: hits.length };
}

/** GET v1/engagement-message-reports/organization/{org}/message/{uuid}: bare array + X-Total-Count. */
export function messageReport(uuid: string, params: URLSearchParams) {
  return receiptPage(receiptHits(params, uuid), params);
}

/** GET v1/engagement-message-reports/organization/{org}: org-wide receipts in the GenericResponse envelope. */
export function organisationReports(params: URLSearchParams) {
  const hits = receiptHits(params);
  const { page, size, offset } = pageOf(params, 20);
  const slice = sortHits(hits, params).slice(offset, offset + size);
  return {
    items: slice.map((hit) => reportRow(hit.unit, hit.i, hit.state)),
    itemPagination: {
      totalElements: hits.length,
      pageSize: size,
      totalPages: Math.max(1, Math.ceil(hits.length / size)),
      pageNumber: page,
      offSet: offset,
    } as IPagination,
  };
}

/** GET /api/engagement-message-reports (JHipster list): bare array + X-Total-Count. */
export function reportList(params: URLSearchParams) {
  return receiptPage(receiptHits(params), params);
}

/** GET /api/engagement-message-reports/count. */
export function reportCount(params: URLSearchParams): number {
  return receiptHits(params).length;
}

type Tally = { male: number; female: number; other: number; young: number; middle: number; old: number; unknownAge: number; total: number };
const emptyTally = (): Tally => ({ male: 0, female: 0, other: 0, young: 0, middle: 0, old: 0, unknownAge: 0, total: 0 });

function tallyPerson(tally: Tally, gender: string | null, age: number | null) {
  tally.total++;
  if (gender === 'MALE') tally.male++;
  else if (gender === 'FEMALE') tally.female++;
  else tally.other++;
  if (age == null) tally.unknownAge++;
  else if (age <= 35) tally.young++;
  else if (age <= 65) tally.middle++;
  else tally.old++;
}

const genderReport = (tally: Tally) => ({
  totalRecipient: tally.total,
  totalMale: tally.male,
  totalFemale: tally.female,
  totalOther: tally.other,
});

const ageReport = (tally: Tally) => ({
  totalRecipient: tally.total,
  ageSixteenThirtyFive: tally.young,
  ageThirtySixSixtyFive: tally.middle,
  ageSixtySixOneTwenty: tally.old,
  ageUnknown: tally.unknownAge,
});

/** GET .../message/{uuid}/charts: MessageChartReport. */
export function messageCharts(uuid: string, params: URLSearchParams) {
  const tally = emptyTally();
  const groups = new Map<string, { groupType: string; groupUuid: string; groupName: string; delivered: number; total: number }>();
  for (const unit of selectUnits(params, uuid)) {
    for (let i = 0; i < unit.rows; i++) {
      const state = statusAt(unit, i);
      const recipient = recipientAt(unit, i);
      tallyPerson(tally, recipient.gender, recipient.age);
      if (recipient.groupUuid) {
        const group = groups.get(recipient.groupUuid) ?? {
          groupType: recipient.groupType ?? '',
          groupUuid: recipient.groupUuid,
          groupName: recipient.group ?? '',
          delivered: 0,
          total: 0,
        };
        group.total++;
        if (state === 'DELIVERED') group.delivered++;
        groups.set(recipient.groupUuid, group);
      }
    }
  }
  return {
    deliveryRateByGroupReport: [...groups.values()].map(({ delivered, total, ...group }) => ({
      ...group,
      deliveryRate: share(delivered, total),
    })),
    genderDistributionPieReport: genderReport(tally),
    ageDistributionPieReport: ageReport(tally),
  };
}

const CARRIERS = [
  { carrierName: 'Mascom Wireless', operatorName: 'MASCOM', mnc: '01' },
  { carrierName: 'Orange Botswana', operatorName: 'ORANGE', mnc: '02' },
  { carrierName: 'BTC Mobile', operatorName: 'BTC', mnc: '04' },
];
const carrierOf = (number: string) => {
  const roll = hashOf(number) % 100;
  return CARRIERS[roll < 52 ? 0 : roll < 85 ? 1 : 2];
};

const NARRATIONS: Record<string, string> = {
  DELIVERED: 'Delivered to handset',
  UNDELIVERABLE: 'Number is not in service',
  REJECTED: 'Rejected by the operator',
  EXPIRED: 'Validity period ended before delivery',
  DELETED: 'Deleted by the SMSC',
  ACCEPTED: 'Accepted by the operator',
  EN_ROUTE: 'On the way to the handset',
  PENDING: 'Waiting for a delivery receipt',
  QUEUED: 'Queued for submission',
  UNKNOWN: 'The operator returned no status',
};

/**
 * GET /api/dashboard/messages/{org}/sms/reports: the SMSC gateway log
 * (List<SmsReportData>, no envelope, no total). The same submissions as the
 * receipt rows, without the do-not-contact skips that never left.
 */
/** Every gateway row for the period in the params, newest first (or oldest with order=asc). */
function gatewayHits(params: URLSearchParams) {
  const daysBack = Number(params.get('daysBack') ?? 0);
  const start = params.get('startDate');
  const end = params.get('endDate');
  const fromMs = start ? Date.parse(`${start}T00:00:00`) : daysBack > 0 ? Date.now() - daysBack * DAY : undefined;
  const toMs = end ? Date.parse(`${end}T23:59:59`) : undefined;
  const hits = receiptHits(new URLSearchParams(), undefined, { excludeSkipped: true, fromMs, toMs });
  const ascending = (params.get('order') ?? 'desc').toLowerCase() === 'asc';
  hits.sort((a, b) => (a.createdMs - b.createdMs || a.i - b.i) * (ascending ? 1 : -1));
  return hits;
}

type GatewayHit = ReturnType<typeof gatewayHits>[number];

function gatewayRow({ unit, i, state }: GatewayHit) {
  const row = reportRow(unit, i, state);
  const carrier = carrierOf(row.mobileNumber);
  const settled = state === 'DELIVERED' || FAILED_STATES.includes(state);
  return {
    uuid: `sms-${row.uuid}`,
    messageId: row.networkMessageId,
    bulkReferenceId: unit.key,
    messageReference: row.uuid,
    submitId: `sub-${row.networkMessageId}`,
    receiptId: settled ? `dlr-${row.networkMessageId}` : null,
    destination: row.mobileNumber.replace(/^\+/, ''),
    sourceAddress: unit.message.from,
    mobileCountryCode: '652',
    mobileCountry: 'Botswana',
    carrierName: carrier.carrierName,
    operatorName: carrier.operatorName,
    clientId: '1042',
    organisationId: MOCK_ORG_ID,
    deliveryStatus: state,
    messageContent: unit.message.content,
    deliveryStatusNarration: NARRATIONS[state] ?? state,
    createdDate: row.createdAt,
    modifiedDate: row.timeStamp,
    deliveryTimestamp: settled ? row.timeStamp : null,
    receiptTimeStamp: settled ? row.timeStamp : null,
    recordStatus: 'ACTIVE',
  };
}

/** The old endpoint: a bare page of rows, no total. */
export function gatewayLog(params: URLSearchParams) {
  return pageSlice(gatewayHits(params), params, 20).map(gatewayRow);
}

function gatewayOutcomeOf(state: string): 'delivered' | 'failed' | 'pending' {
  if (state === 'DELIVERED') return 'delivered';
  return FAILED_STATES.includes(state) ? 'failed' : 'pending';
}

/** /sms/reports/page: a real page of the period with its total, filtered by outcome and number. */
export function gatewayPage(params: URLSearchParams) {
  const status = (params.get('status') ?? '').toLowerCase();
  const search = (params.get('search') ?? '').replace(/\D/g, '');
  let hits = gatewayHits(params);
  if (status === 'delivered' || status === 'failed' || status === 'pending') {
    hits = hits.filter((hit) => gatewayOutcomeOf(hit.state) === status);
  }
  let rows = hits.map(gatewayRow);
  if (search) rows = rows.filter((row) => row.destination.includes(search));
  const size = Math.min(Math.max(Number(params.get('size') ?? 25) || 25, 1), 500);
  const page = Math.max(Number(params.get('page') ?? 0) || 0, 0);
  return { items: rows.slice(page * size, page * size + size), total: rows.length, page, size };
}

/** /sms/reports/summary: outcome counts for the period. */
export function gatewaySummary(params: URLSearchParams) {
  const summary = { total: 0, delivered: 0, failed: 0, pending: 0 };
  for (const hit of gatewayHits(params)) {
    summary.total += 1;
    summary[gatewayOutcomeOf(hit.state)] += 1;
  }
  return summary;
}

// ------------------------------------------------ dashboard: messages sent

/** Steady 0..1 noise: the same input always gives the same number. */
function steadyNoise(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** How busy each hour of the day is, relative to the day's average. */
const HOUR_SHAPE = [0.1, 0.05, 0.03, 0.03, 0.05, 0.2, 0.6, 1.2, 2.2, 2.6, 2.4, 2.1, 1.8, 2.0, 2.2, 2.0, 1.7, 1.4, 1.1, 0.8, 0.6, 0.4, 0.25, 0.15];

/** Recipients the sandbox organisation reached from the portal in the hour starting at `start`. */
function sentInHour(start: Date): number {
  const weekend = start.getDay() === 0 || start.getDay() === 6;
  const variation = 0.6 + 0.8 * steadyNoise(Math.floor(start.getTime() / 3_600_000));
  return Math.round((weekend ? 9 : 26) * HOUR_SHAPE[start.getHours()] * variation);
}

/** SentOverview (GET .../dashboard/messages/{org}/sent/overview): buckets in the browser's own time. */
export function sentOverview(params: URLSearchParams) {
  const from = new Date(params.get('from') ?? '');
  const to = new Date(params.get('to') ?? '');
  const interval = params.get('interval') ?? 'day';
  const HOUR = 3_600_000;
  const truncate = (time: Date) => {
    if (interval === 'hour') return new Date(Math.floor(time.getTime() / HOUR) * HOUR);
    const day = new Date(time.getFullYear(), time.getMonth(), time.getDate());
    if (interval === 'week') day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
    if (interval === 'month') day.setDate(1);
    return day;
  };
  const next = (start: Date) => {
    if (interval === 'hour') return new Date(start.getTime() + HOUR);
    if (interval === 'week') return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
    if (interval === 'month') return new Date(start.getFullYear(), start.getMonth() + 1, 1);
    return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
  };

  const now = Date.now();
  const buckets: { start: string; total: number; delivered: number; failed: number }[] = [];
  for (let start = truncate(from); start < to && buckets.length < 745; start = next(start)) {
    const end = Math.min(next(start).getTime(), to.getTime(), now);
    let total = 0;
    let delivered = 0;
    let failed = 0;
    for (let hour = Math.floor(Math.max(start.getTime(), from.getTime()) / HOUR) * HOUR; hour < end; hour += HOUR) {
      const sent = sentInHour(new Date(hour));
      // Receipts take a while: the last couple of hours still have many without one.
      const age = now - hour;
      const noReceipt = Math.round(sent * (age < 2 * HOUR ? 0.35 : age < 48 * HOUR ? 0.06 : 0.01));
      const lost = Math.round(sent * (0.05 + 0.04 * steadyNoise(hour / HOUR + 7)));
      total += sent;
      failed += lost;
      delivered += Math.max(0, sent - lost - noReceipt);
    }
    buckets.push({ start: start.toISOString(), total, delivered, failed });
  }
  const sum = (key: 'total' | 'delivered' | 'failed') => buckets.reduce((all, bucket) => all + bucket[key], 0);
  const total = sum('total');
  const delivered = sum('delivered');
  const failed = sum('failed');
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    interval,
    zone: params.get('zone') ?? 'UTC',
    total,
    delivered,
    failed,
    pending: Math.max(0, total - delivered - failed),
    buckets,
  };
}

// ------------------------------------------------ message/campaign summaries

/** CampaignReceipientsData for a broadcast (GET .../engagement-messages/{uuid}/receipients). */
export function messageRecipients(key: string) {
  const message = findAnyMessage(key);
  if (!message) return undefined;
  const campaign = MOCK_CAMPAIGNS.find((candidate) =>
    candidate.engagementMessages.some((item) => item.uuid === message.uuid)
  );
  if (campaign) return campaignRecipients(campaign);
  const lists = listsOf(message.engagementContactLists);
  const planned = PLANNED_RECIPIENTS.get(message.uuid);
  const total = SMART_UUIDS.has(message.uuid)
    ? 1
    : planned ?? (findMessage(message.uuid) ? broadcastSize(message.uuid) : audienceOf(lists).length);
  return recipientsData(lists, total);
}

/** Sends of the campaign's current schedules (slots removed in an edit drop out). */
function campaignUnits(campaign: MockCampaign) {
  const current = new Set(
    campaign.engagementMessages.flatMap((message) => message.engagementMessageSchedules.map((schedule) => schedule.uuid))
  );
  return sendUnits().filter((unit) => unit.campaign?.uuid === campaign.uuid && current.has(unit.key));
}

/** GET .../campaign/{uuid}/receipients. */
export function campaignRecipients(campaign: MockCampaign) {
  const lists = listsOf(campaign.engagementContactLists);
  return recipientsData(lists, audienceOf(lists).length);
}

/** GET .../campaign/{uuid}/details/stat: EngagementCampaignStats. */
export function campaignStats(campaign: MockCampaign) {
  const summary = summarise(campaignUnits(campaign));
  return {
    recipientsCount: audienceOf(listsOf(campaign.engagementContactLists)).length,
    messagesCount: campaign.engagementMessages.length,
    scheduledCount: campaign.engagementMessages
      .flatMap((message) => message.engagementMessageSchedules)
      .filter((schedule) => schedule.status === 'PENDING').length,
    deliveredPercent: share(summary.delivered, summary.total),
  };
}

const wholePercent = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : 0);

/** GET v1/engagement-campaign-message-reports/.../campaign/{uuid}: CampaignReport[]. */
export function campaignMessagesReport(campaign: MockCampaign) {
  const audience = audienceOf(listsOf(campaign.engagementContactLists)).length;
  const units = campaignUnits(campaign);
  return campaign.engagementMessages.map((message) => ({
    message: message.content,
    referenceId: message.uuid,
    reports: message.engagementMessageSchedules.map((schedule) => {
      const unit = units.find((candidate) => candidate.key === schedule.uuid);
      const summary = summarise(unit ? [unit] : []);
      const total = unit?.rows || audience;
      return {
        reportUuid: `report-${schedule.uuid}`,
        organizationUuid: MOCK_ORG_ID,
        campaignUuid: campaign.uuid,
        messageUuid: message.uuid,
        scheduleUuid: schedule.uuid,
        content: message.content,
        messageSchedules: `${schedule.scheduleDate}T${schedule.scheduleTime}:00`,
        scheduleStatus: schedule.status,
        totalRecipient: total,
        deliveryRate: wholePercent(summary.delivered, summary.total),
        uuid: `report-${schedule.uuid}`,
        total_recipient: total,
      };
    }),
  }));
}

/** The campaign's groups with their members. */
function campaignGroups(campaign: MockCampaign) {
  const lists = listsOf(campaign.engagementContactLists);
  const groups: { groupType: string; groupUuid: string; groupName: string; members: MockContact[] }[] = [];
  const add = (kind: GroupKind, groupType: string, keys: string[]) => {
    for (const key of keys) {
      const group = findGroup(kind, key);
      if (group) groups.push({ groupType, groupUuid: group.uuid, groupName: group.name, members: groupMembers(kind, key) });
    }
  };
  add('companies', 'COMPANY', lists.companies);
  add('tags', 'TAG', lists.tags);
  add('segments', 'SEGMENT', lists.segments);
  return groups;
}

/** GET v1/engagement-campaign-recipient-reports/.../campaign/{uuid}: CampaignRecipientReport[]. */
export function campaignRecipientsReport(campaign: MockCampaign) {
  return campaignGroups(campaign).map(({ members, ...group }) => {
    const tally = emptyTally();
    members.forEach((contact) => tallyPerson(tally, contact.gender, contact.age));
    return {
      ...group,
      totalRecipient: tally.total,
      totalMale: tally.male,
      totalFemale: tally.female,
      other: tally.other,
      ageSixteenThirtyFive: tally.young,
      ageThirtySixSixtyFive: tally.middle,
      ageSixtySixOneTwenty: tally.old,
    };
  });
}

/** GET .../campaign/{uuid}/messages/charts. */
export function campaignMessagesCharts(campaign: MockCampaign) {
  const units = campaignUnits(campaign);
  const tally = emptyTally();
  audienceOf(listsOf(campaign.engagementContactLists)).forEach(({ contact }) => tallyPerson(tally, contact.gender, contact.age));
  return {
    campaignMessageBreakDownGraph: campaign.engagementMessages.map((message) => {
      const summary = summarise(units.filter((unit) => unit.message.uuid === message.uuid));
      return { message: message.messageSubject, deliveryRate: wholePercent(summary.delivered, summary.total) };
    }),
    GenderDistributionReport: genderReport(tally),
  };
}

/** GET .../campaign/{uuid}/recipient/charts. */
export function campaignRecipientCharts(campaign: MockCampaign) {
  const units = campaignUnits(campaign);
  const tally = emptyTally();
  audienceOf(listsOf(campaign.engagementContactLists)).forEach(({ contact }) => tallyPerson(tally, contact.gender, contact.age));
  return {
    deliveryRateByGroup: campaignGroups(campaign).map(({ members, ...group }) => {
      const memberSet = new Set(members.map((contact) => contact.uuid));
      let delivered = 0;
      let total = 0;
      for (const unit of units) {
        if (!unit.rows) continue;
        unit.audience.forEach((member, i) => {
          if (!memberSet.has(member.contact.uuid)) return;
          total++;
          if (statusAt(unit, i) === 'DELIVERED') delivered++;
        });
      }
      return { ...group, deliveryRate: wholePercent(delivered, total) };
    }),
    ageDistributionPieReport: ageReport(tally),
  };
}

// --------------------------------------------------------------- templates

const TEMPLATE_PLAN: [string, string, string][] = [
  ['Welcome SMS', 'Informational', 'Welcome to Black Konnect, {{firstName}}. Dial *123# any time to check your account.\nBLACK'],
  ['Bundle purchase confirmation', 'Transactional', 'You bought 5GB for P50. It expires on {{expiryDate}}. Dial *150# to check your balance.\nBLACK'],
  ['Payment reminder', 'Transactional', 'Dumela {{firstName}}, your bill of P{{amount}} is due on {{dueDate}}. Pay with Orange Money or at any branch.\nBLACK'],
  ['One-time PIN', 'Transactional', 'Your verification code is {{code}}. It expires in 10 minutes. Never share it with anyone.'],
  ['Survey invite', 'Informational', 'How did we do? Reply with a number from 0 to 10. Replies are free.\nBLACK'],
  ['Service outage notice', 'Informational', 'We are fixing a network fault in {{city}}. Service should be back by 18:00. Sorry for the disruption.\nBLACK'],
  ['Weekend data promo', 'Promotional', 'This weekend only: 2GB for P20. Dial *150*2# before Sunday midnight.\nBLACK'],
  ['Daily trivia question', 'Promotional', 'Your free SOKA question is waiting. Answer correctly to enter Friday\'s P5,000 draw.\nSOKA'],
  ['Account safety tips', 'Educational', 'Never share your PIN or one-time code. Black Konnect staff will never ask for it.\nBLACK'],
];

const templateRand = stream(1701);

export const MOCK_TEMPLATES = TEMPLATE_PLAN.map(([name, category, content], i) => {
  const createdAt = ago(templateRand.int(20, 300));
  const updatedAt = ago(templateRand.int(0, 19));
  return {
    id: i + 1,
    uuid: `template-${pad4(i + 1)}`,
    name,
    title: name,
    description: `${category} template: ${name.toLowerCase()}`,
    content,
    category,
    templateType: 'SMS',
    contentType: 'TEXT',
    messageType: 'SMS',
    requestTypeId: null as number | null,
    communicationsCount: templateRand.int(0, 48),
    permittedForUser: true,
    unlayerTemplate: false,
    organisationId: MOCK_ORG_ID,
    preview: null as unknown,
    createdBy: ME,
    modifiedBy: ME,
    createdAt,
    updatedAt,
    modifiedAt: updatedAt,
    engagementMessageFieldDefinitions: [] as unknown[],
  };
});

export const findTemplate = (key: string) => byKey(MOCK_TEMPLATES, key);

const TEMPLATE_SORTS: Record<string, string> = {
  nameAsc: 'name,asc',
  nameDesc: 'name,desc',
  createdAsc: 'createdAt,asc',
  createdDesc: 'createdAt,desc',
  modifiedAsc: 'updatedAt,asc',
  modifiedDesc: 'updatedAt,desc',
};

/** GET message-templates/organizations/{org}: a Spring-style page with `content` and `currentPage`. */
export function templatePage(params: URLSearchParams) {
  const term = (params.get('template_name') ?? '').toLowerCase();
  const sort = TEMPLATE_SORTS[params.get('sortBy') ?? ''] ?? 'createdAt,desc';
  const matching = sortList(
    MOCK_TEMPLATES.filter((template) => !term || template.name.toLowerCase().includes(term)),
    new URLSearchParams({ sort })
  );
  const { page, size, offset } = pageOf(params, 10);
  const totalPages = Math.max(1, Math.ceil(matching.length / size));
  return {
    content: matching.slice(offset, offset + size),
    totalElements: matching.length,
    totalPages,
    currentPage: page,
    pageSize: size,
    hasNext: page + 1 < totalPages,
  };
}

export function newTemplate(body: any) {
  const id = Math.max(...MOCK_TEMPLATES.map((template) => template.id)) + 1;
  return { ...MOCK_TEMPLATES[0], ...body, id, uuid: `template-${pad4(id)}`, createdAt: iso(Date.now()), updatedAt: iso(Date.now()) };
}

/** Merge fields organisations can personalise with (engagement-message-field-definitions). */
export const MOCK_FIELD_DEFINITIONS = [
  ['firstName', 'First name', 'TEXT', 'PERSON_ENTITY'],
  ['lastName', 'Last name', 'TEXT', 'PERSON_ENTITY'],
  ['fullName', 'Full name', 'TEXT', 'PERSON_ENTITY'],
  ['primaryContactNumber', 'Phone number', 'TEXT', 'PERSON_ENTITY'],
  ['emailAddress', 'Email', 'TEXT', 'PERSON_ENTITY'],
  ['city', 'City', 'TEXT', 'PERSON_ENTITY'],
  ['accountNumber', 'Account number', 'TEXT', 'CUSTOM'],
  ['branch', 'Branch', 'TEXT', 'CUSTOM'],
  ['entries', 'Draw entries', 'NUMBER', 'CUSTOM'],
].map(([key, displayName, dataType, objectType], i) => ({
  id: i + 1,
  uuid: `field-${pad4(i + 1)}`,
  name: key,
  key,
  displayName,
  dataType,
  objectType,
  organisationId: MOCK_ORG_ID,
  engagementTemplates: [] as unknown[],
}));

/** Numbers on the restriction list (engagement-contact-restriction-logs). */
export const MOCK_RESTRICTION_LOGS = [3, 8, 17, 26, 31, 44].map((n, i) => {
  const contact = MOCK_CONTACTS[n];
  return {
    id: i + 1,
    uuid: `restriction-${pad4(i + 1)}`,
    contact: contact.primaryContactNumber,
    contactInformation: contact.primaryContactNumber,
    contactType: 'MSISDN',
    reason: i % 3 === 0 ? 'Replied STOP' : 'Opted out by operator request',
    organisationId: MOCK_ORG_ID,
    createdAt: ago(4 + i * 9),
  };
});

// ------------------------------------------------------------- org / users

export const MOCK_USER_PROFILE = {
  id: MOCK_USER_ID,
  username: 'dev.user',
  firstName: 'Dev',
  lastName: 'User',
  email: 'dev.user@blackkonnect.local',
  emailVerified: true,
  attributes: {
    phoneNumber: ['+26771234567'],
    organisationId: [MOCK_ORG_ID],
  },
  userProfileMetadata: { attributes: [], groups: [] },
  self: null,
  origin: null,
  createdTimestamp: NOW - 365 * DAY,
  enabled: true,
  totp: false,
  federationLink: null,
  serviceAccountClientId: null,
  credentials: null,
  disableableCredentialTypes: [],
  requiredActions: [],
  federatedIdentities: null,
  realmRoles: [
    'SUPER_ADMIN',
    'ROLE_BLKKCXP_ADMINISTRATOR',
    'ROLE_BLKKCXP_ACCOUNT_MANAGER',
    'ROLE_BLKKCXP_CONTACTS_MANAGER',
    'ROLE_BLKKCXP_CONTENT_MANAGER',
    'ROLE_BLKKCXP_INTEGRATIONS_MANAGER',
    'ROLE_BLKKCXP_FINANCE_DATA_MANAGER',
  ],
  clientRoles: null,
  clientConsents: null,
  notBefore: 0,
  applicationRoles: null,
  socialLinks: null,
};

export const MOCK_ORGANIZATION = {
  id: MOCK_ORG_ID,
  name: MOCK_ORG_NAME,
  alias: 'blackkonnect-dev',
  enabled: true,
  description: 'Local development tenant backed by mock data',
  website: 'https://blackkonnect.io',
  email: 'support@blackkonnect.io',
  country: 'Botswana',
  countryCode: 'BW',
  registrationNumber: 'BW00001234567',
  size: '11-50',
  attributes: {
    serviceAccountId: '22222222-2222-4222-8222-222222222222',
    organisationId: MOCK_ORG_ID,
    logoImageId: '33333333-3333-4333-8333-333333333333',
    blackkonnectSmscTenantId: '1042',
    primaryContactNumber: '+2673951234',
    email: 'support@blackkonnect.io',
    address: 'Plot 54368, Central Business District',
    city: 'Gaborone',
    industryType: 'TELECOMMUNICATIONS',
  } as Record<string, string>,
};

type MemberState = 'active' | 'disabled' | 'invited';

const MEMBER_PLAN: [string, string, string, string[], MemberState][] = [
  ['Dev', 'User', 'dev.user', ['SUPER_ADMIN'], 'active'],
  ['Naledi', 'Sebego', 'n.sebego', ['ROLE_BLKKCXP_ADMINISTRATOR'], 'active'],
  ['Tebogo', 'Kgosi', 't.kgosi', ['ROLE_BLKKCXP_CONTACTS_MANAGER'], 'active'],
  ['Mpho', 'Moremi', 'm.moremi', ['ROLE_BLKKCXP_CONTENT_MANAGER', 'ROLE_BLKKCXP_CONTACTS_MANAGER'], 'active'],
  ['Lesego', 'Tau', 'l.tau', ['ROLE_BLKKCXP_FINANCE_DATA_MANAGER'], 'active'],
  ['Kefilwe', 'Dikgang', 'k.dikgang', ['ROLE_BLKKCXP_ACCOUNT_MANAGER'], 'disabled'],
  ['Onalenna', 'Mmusi', 'o.mmusi', ['ROLE_BLKKCXP_INTEGRATIONS_MANAGER'], 'invited'],
  ['Thato', 'Serame', 't.serame', ['ROLE_BLKKCXP_CONTENT_MANAGER'], 'disabled'],
  ['Goitseone', 'Phiri', 'g.phiri', ['ROLE_BLKKCXP_CONTACTS_MANAGER'], 'invited'],
];

function makeMember(
  index: number,
  firstName: string,
  lastName: string,
  username: string,
  roles: string[],
  state: MemberState,
  createdMs: number
) {
  return {
    id: index === 0 ? MOCK_USER_ID : `user-${pad4(index + 1)}`,
    username,
    firstName,
    lastName,
    email: `${username}@blackkonnect.local`,
    // users-table.component.html reads `emailAddress`; other views read `email`.
    emailAddress: `${username}@blackkonnect.local`,
    emailVerified: state !== 'invited',
    enabled: state !== 'disabled',
    totp: false,
    requiredActions: state === 'invited' ? ['VERIFY_EMAIL', 'UPDATE_PASSWORD'] : ([] as string[]),
    createdTimestamp: createdMs,
    attributes: {
      organisationId: [MOCK_ORG_ID],
      phoneNumber: [phone(900 + index)],
    } as Record<string, string[]>,
    realmRoles: roles,
  };
}

export const MOCK_MEMBERS = MEMBER_PLAN.map(([firstName, lastName, username, roles, state], i) =>
  makeMember(i, firstName, lastName, username, roles, state, NOW - (360 - i * 37) * DAY)
);

export type MockMember = (typeof MOCK_MEMBERS)[number];

export const findMember = (key: string) =>
  MOCK_MEMBERS.find((member) => member.id === key || member.username === key || member.email === key);

/** Team list: `firstName.contains`-style criteria, `enabled=true|false`, sort and paging. */
export function membersPage(params: URLSearchParams) {
  const enabled = params.get('enabled');
  const matching = query(
    MOCK_MEMBERS.filter((member) => enabled == null || String(member.enabled) === enabled),
    params,
    { sort: 'createdTimestamp,desc' }
  );
  return paginate(matching, params, 10);
}

export function createMember(body: any): MockMember {
  const index = MOCK_MEMBERS.length;
  const email = String(body?.emailAddress ?? body?.email ?? `user${index}@blackkonnect.local`);
  const member = makeMember(
    index,
    String(body?.firstName ?? ''),
    String(body?.lastName ?? ''),
    email.split('@')[0],
    Array.isArray(body?.authorities) ? body.authorities : [],
    'invited',
    Date.now()
  );
  member.email = email;
  member.emailAddress = email;
  if (body?.phoneNumber) member.attributes['phoneNumber'] = [String(body.phoneNumber)];
  MOCK_MEMBERS.push(member);
  return member;
}

export const MOCK_ROLES = [
  ['SUPER_ADMIN', 'Full access to every portal capability'],
  ['ROLE_BLKKCXP_ADMINISTRATOR', 'Administer the organisation and its users'],
  ['ROLE_BLKKCXP_ACCOUNT_MANAGER', 'Manage account and billing settings'],
  ['ROLE_BLKKCXP_CONTACTS_MANAGER', 'Create and maintain contacts'],
  ['ROLE_BLKKCXP_CONTENT_MANAGER', 'Author messages, templates and campaigns'],
  ['ROLE_BLKKCXP_INTEGRATIONS_MANAGER', 'Manage API keys and integrations'],
  ['ROLE_BLKKCXP_FINANCE_DATA_MANAGER', 'Access finance and billing reports'],
].map(([name, description], i) => ({
  id: `role-${pad4(i + 1)}`,
  name,
  description,
  scopeParamRequired: null,
  composite: false,
  composites: null,
  clientRole: false,
  containerId: 'blackkonnect',
  attributes: null,
}));

/** Monochrome placeholder images, so the "image present" state can be designed. */
export const ORGANIZATION_LOGO_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256">' +
  '<rect width="256" height="256" rx="56" fill="#0a0a0a"/>' +
  '<text x="128" y="152" text-anchor="middle" font-family="Comfortaa, sans-serif" font-size="88" font-weight="700" fill="#ffffff">BK</text></svg>';

export const PROFILE_IMAGE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256">' +
  '<rect width="256" height="256" fill="#e5e5e5"/>' +
  '<circle cx="128" cy="100" r="44" fill="#737373"/>' +
  '<path d="M44 236c8-48 44-76 84-76s76 28 84 76" fill="#737373"/></svg>';

/** Whether each image is currently "uploaded" (DELETE clears it, POST restores it). */
export const IMAGE_STATE = { logo: true, profileImage: true };

// ---------------------------------------------------------- test contacts

const TEST_CONTACT_PLAN: [string, string, 'MALE' | 'FEMALE', string][] = [
  ['Dev', 'User', 'MALE', 'Gaborone'],
  ['Naledi', 'Sebego', 'FEMALE', 'Gaborone'],
  ['Tebogo', 'Kgosi', 'MALE', 'Francistown'],
  ['Amantle', 'Baruti', 'FEMALE', 'Maun'],
  ['Oarabile', 'Segwai', 'MALE', 'Palapye'],
];

const testRand = stream(1801);

export const MOCK_TEST_CONTACTS = TEST_CONTACT_PLAN.map(([firstName, lastName, gender, city], i) => {
  const createdAt = ago(testRand.int(5, 90));
  return {
    id: i + 1,
    uuid: `test-contact-${pad4(i + 1)}`,
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
    primaryContactNumber: phone(900 + i),
    emailAddress: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@blackkonnect.local`,
    gender,
    birthDate: ago(testRand.int(8000, 16000)).slice(0, 10),
    city,
    region: districtOf(city),
    country: 'Botswana',
    countryCode: 'BW',
    organizationUuid: MOCK_ORG_ID,
    createdBy: ME.login,
    createdAt,
    modifiedAt: createdAt,
  };
});

export type MockTestContact = (typeof MOCK_TEST_CONTACTS)[number];

export const findTestContact = (key: string) => byKey(MOCK_TEST_CONTACTS, key);

export function createTestContact(body: any): MockTestContact {
  const id = Math.max(0, ...MOCK_TEST_CONTACTS.map((contact) => contact.id)) + 1;
  const firstName = String(body?.firstName ?? '');
  const lastName = String(body?.lastName ?? '');
  const contact: MockTestContact = {
    ...MOCK_TEST_CONTACTS[0],
    ...body,
    id,
    uuid: `test-contact-${pad4(id)}`,
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim(),
    createdAt: iso(Date.now()),
    modifiedAt: iso(Date.now()),
  };
  MOCK_TEST_CONTACTS.push(contact);
  return contact;
}

/** POST .../message-test-contacts/send-test-message: TestMessageResponseDTO. */
export function sendTestMessage(body: any) {
  const contact = findTestContact(String(body?.testContactId ?? ''));
  if (!contact) return undefined;
  return {
    status: 'SENT',
    message: `Test message sent to ${contact.fullName} (${contact.primaryContactNumber}).`,
    deliveryStatus: 'ACCEPTED',
    correlationId: `test-${hashOf(`${contact.uuid}:${body?.message ?? ''}`).toString(16)}`,
  };
}

// --------------------------------------------------------------- sender IDs

/** ISenderID[]: status 1 is approved, 0 is awaiting approval; type 2 is a short code. */
export const MOCK_SENDER_IDS = (
  [
    ['BLACK', 1, 1],
    ['SOKA', 1, 1],
    ['16000', 1, 2],
    ['ZEBRAS', 1, 1],
    ['BLACKKONNECT', 1, 1],
    ['BK-ALERTS', 0, 1],
  ] as [string, number, number][]
).map(([senderId, status, senderIdType], i) => ({
  id: i + 1,
  clientId: 1042,
  senderId,
  parentId: 0,
  dateCreated: ago(300 - i * 40),
  dateModified: ago(20 - i * 3),
  generate0ptOutMessage: 'false',
  exemptFromBlacklistCheck: senderIdType === 2 ? 'true' : 'false',
  createdBy: 1,
  updatedBy: 1,
  status,
  senderIdType,
  addedByClient: 1,
}));

// ------------------------------------------------------------------ stats

/** GET /api/dashboard/{org}/stats, worked out from the fixtures. */
export function dashboardStats() {
  return {
    campaignsCount: MOCK_CAMPAIGNS.filter((campaign) => campaign.status === 'ACTIVE').length,
    contactsCount: MOCK_CONTACTS.length,
    usersCount: MOCK_MEMBERS.length,
    pendingMessagesCount: MOCK_MESSAGES.filter((message) => message.status === 'Pending' && !message.archive).length,
  };
}

// --------------------------------------------------------------------- USSD

type UssdEnv = 'DEVELOPMENT' | 'SANDBOX' | 'UAT' | 'PRODUCTION';

const USSD_PLAN: {
  name: string;
  alias: string;
  serviceCode: string;
  environment: UssdEnv;
  enabled: boolean;
  auth: 'NONE' | 'BASIC_AUTH' | 'BEARER_TOKEN' | 'API_KEY';
  url: string;
  description: string;
  created: number;
}[] = [
  { name: 'SOKA Trivia', alias: 'soka-trivia', serviceCode: '*123#', environment: 'PRODUCTION', enabled: true, auth: 'BEARER_TOKEN', url: 'https://api.soka.example.co.bw/ussd/session', description: 'Daily football trivia with weekly prize draws', created: 210 },
  { name: 'Zebras Predictor', alias: 'zebras-predictor', serviceCode: '*150*10#', environment: 'UAT', enabled: true, auth: 'API_KEY', url: 'https://predict.zebras.example.co.bw/ussd', description: 'Score predictions for national team matches', created: 64 },
  { name: 'Balance and Entries', alias: 'balance-entries', serviceCode: '*150*11#', environment: 'SANDBOX', enabled: true, auth: 'BASIC_AUTH', url: 'https://api.soka.example.co.bw/ussd/balance', description: 'Check draw entries and winnings', created: 41 },
  { name: 'Rollers Club Codes', alias: 'rollers-codes', serviceCode: '*150*12#', environment: 'DEVELOPMENT', enabled: true, auth: 'NONE', url: 'https://dev.rollers.example.co.bw/ussd', description: 'Back a club to double weekly entries', created: 12 },
  { name: 'Payday Draw Opt-in', alias: 'payday-optin', serviceCode: '*150*13#', environment: 'DEVELOPMENT', enabled: false, auth: 'NONE', url: 'https://api.soka.example.co.bw/ussd/payday', description: 'Opt in to the monthly payday draw', created: 30 },
  { name: 'Customer Survey', alias: 'customer-survey', serviceCode: '*150*14#', environment: 'SANDBOX', enabled: true, auth: 'BEARER_TOKEN', url: 'https://survey.blackkonnect.example/ussd', description: 'Net promoter score survey after support calls', created: 22 },
];

const MNOS = ['MASCOM', 'ORANGE', 'BTC'];

function makeUssdApp(plan: (typeof USSD_PLAN)[number], i: number) {
  const id = String(101 + i);
  const createdAt = ago(plan.created);
  const auth = plan.auth;
  return {
    id,
    uuid: `ussd-app-${pad4(i + 1)}`,
    name: plan.name,
    alias: plan.alias,
    serviceCode: plan.serviceCode,
    organisationId: MOCK_ORG_ID,
    description: plan.description,
    enabled: plan.enabled,
    environment: plan.environment as string,
    countryName: 'Botswana',
    countryCode: 'BW',
    internationalCode: '+267',
    callbackUrl1: plan.url,
    callbackUrl2: i % 2 === 0 ? plan.url.replace('://', '://backup.') : '',
    callbackServiceName: plan.alias,
    callbackContentType: 'application/json',
    callbackMethod: 'POST',
    callbackAuthType: auth as string,
    callbackBearerToken: auth === 'BEARER_TOKEN' ? `example-token-${plan.alias}` : null,
    callbackBasicUsername: auth === 'BASIC_AUTH' ? `${plan.alias}-ussd` : null,
    callbackBasicPassword: auth === 'BASIC_AUTH' ? 'example-password' : null,
    callbackApiKey: auth === 'API_KEY' ? `example-key-${plan.alias}` : null,
    callbackApiKeyHeader: auth === 'API_KEY' ? 'X-Api-Key' : null,
    callbackCustomAuthHeader: null as string | null,
    callbackCustomAuthValue: null as string | null,
    mnoServiceDataPassThrough: i % 3 === 0,
    passthrough: i % 2 === 1,
    environmentAutoPromotion: plan.environment === 'DEVELOPMENT',
    createdAt,
    modifiedAt: ago(Math.max(0, plan.created - 5 - i)),
    routingConfigurations: [null, ...MNOS.slice(0, i % 3)].map((mno, k) => ({
      id: `${id}-route-${k + 1}`,
      serviceCode: plan.serviceCode,
      serviceSubCode: mno ? String(k) : undefined,
      targetUrl: mno ? `${plan.url}/${mno.toLowerCase()}` : plan.url,
      targetApplication: plan.alias,
      mno: mno ?? undefined,
      countryCode: 'BW',
      version: 1 + k,
      isActive: plan.enabled,
      isDefault: !mno,
      passThrough: false,
      createdAt,
      modifiedAt: createdAt,
      organisationId: MOCK_ORG_ID,
    })),
    ussdApplicationMsisdnImpersonations: [
      {
        id: `${id}-impersonation`,
        enabled: plan.environment !== 'PRODUCTION',
        sandboxOnly: true,
        durationUnit: 'MINUTES',
        timeToLiveDuration: 30,
        ussdImpersonationContexts: [] as unknown[],
      },
    ],
    ussdOrganisationProfile: {
      id: `profile-${MOCK_ORG_ID.slice(-4)}`,
      organisationId: MOCK_ORG_ID,
      organisationName: MOCK_ORG_NAME,
      referenceId: 'BK-DEV-0001',
      accountName: MOCK_ORG_NAME,
      serviceAccountId: MOCK_ORGANIZATION.attributes['serviceAccountId'],
      countryCode: 'BW',
      countryName: 'Botswana',
      createdAt,
      modifiedAt: createdAt,
      status: 'ACTIVE',
    },
  };
}

export const MOCK_USSD_APPS = USSD_PLAN.map(makeUssdApp);
export type MockUssdApp = (typeof MOCK_USSD_APPS)[number];

export const findUssdApp = (key: string) => byKey(MOCK_USSD_APPS, key);

const ENV_ORDER: UssdEnv[] = ['DEVELOPMENT', 'SANDBOX', 'UAT', 'PRODUCTION'];

function makePromotion(app: MockUssdApp, n: number, source: string, target: string, status: string, daysAgo: number, notes: string) {
  const requestedAt = ago(daysAgo);
  const reviewed = status !== 'PENDING_APPROVAL';
  return {
    id: `${app.id}-promotion-${n}`,
    promotionId: `PRM-${app.id}-${pad4(n)}`,
    sourceEnvironment: source,
    targetEnvironment: target,
    version: `v${n}`,
    action: 'PROMOTE',
    status,
    requestedBy: n % 2 ? 'Dev User' : 'Mpho Moremi',
    requestedAt,
    reviewedBy: reviewed ? 'Naledi Sebego' : undefined,
    reviewedAt: reviewed ? ago(Math.max(0, daysAgo - 1)) : undefined,
    promotedAt: status === 'PROMOTED' ? ago(Math.max(0, daysAgo - 1)) : undefined,
    promotionNotes: notes,
  };
}

const PROMOTIONS = new Map<string, ReturnType<typeof makePromotion>[]>(
  MOCK_USSD_APPS.map((app) => {
    const reached = ENV_ORDER.indexOf(app.environment as UssdEnv);
    const history: ReturnType<typeof makePromotion>[] = [];
    for (let step = 1; step <= reached; step++) {
      history.push(
        makePromotion(app, step, ENV_ORDER[step - 1], ENV_ORDER[step], 'PROMOTED', 60 - step * 12, `Promoted after testing in ${ENV_ORDER[step - 1].toLowerCase()}`)
      );
    }
    if (app.environment === 'UAT') {
      history.push(makePromotion(app, reached + 1, 'UAT', 'PRODUCTION', 'PENDING_APPROVAL', 1, 'Ready for the Zebras v Uganda match day'));
    }
    if (app.alias === 'payday-optin') {
      history.push(makePromotion(app, 1, 'DEVELOPMENT', 'SANDBOX', 'REJECTED', 6, 'Callback returned 500 on the opt-in step'));
    }
    return [app.id, history.reverse()] as [string, ReturnType<typeof makePromotion>[]];
  })
);

export const ussdPromotions = (app: MockUssdApp) => PROMOTIONS.get(app.id) ?? [];

/** POST .../{id}/promote: production waits for approval; other targets are approved at once. */
export function promoteUssdApp(app: MockUssdApp, body: any) {
  const history = PROMOTIONS.get(app.id) ?? [];
  const target = String(body?.targetEnvironment ?? 'SANDBOX');
  const status = target === 'PRODUCTION' ? 'PENDING_APPROVAL' : 'PROMOTED';
  const record = {
    ...makePromotion(app, history.length + 1, String(body?.sourceEnvironment ?? app.environment), target, status, 0, String(body?.promotionNotes ?? '')),
    requestedBy: String(body?.requestedBy ?? 'Dev User'),
  };
  if (status === 'PROMOTED') app.environment = target;
  PROMOTIONS.set(app.id, [record, ...history]);
  return record;
}

export function createUssdApp(body: any): MockUssdApp {
  const index = MOCK_USSD_APPS.length;
  const app = makeUssdApp(
    {
      name: String(body?.name ?? 'New application'),
      alias: String(body?.alias ?? `app-${index + 1}`),
      serviceCode: String(body?.serviceCode ?? `*384*${1000 + index}#`),
      environment: 'DEVELOPMENT',
      enabled: true,
      auth: 'NONE',
      url: String(body?.callbackUrl1 ?? ''),
      description: String(body?.description ?? ''),
      created: 0,
    },
    index
  );
  Object.assign(app, body, { id: app.id, uuid: app.uuid, enabled: true, environment: body?.environment ?? 'DEVELOPMENT' });
  MOCK_USSD_APPS.push(app);
  PROMOTIONS.set(app.id, []);
  return app;
}

export function ussdStats() {
  return {
    organisationId: MOCK_ORG_ID,
    totalApplications: MOCK_USSD_APPS.length,
    enabledApplications: MOCK_USSD_APPS.filter((app) => app.enabled).length,
    disabledApplications: MOCK_USSD_APPS.filter((app) => !app.enabled).length,
    pendingPromotions: [...PROMOTIONS.values()].flat().filter((record) => record.status === 'PENDING_APPROVAL').length,
  };
}

let simulationCodes = 37;

export function generateServiceCode(countryCode: string) {
  simulationCodes++;
  return {
    serviceCode: `*384*${1000 + simulationCodes}#`,
    countryCode: countryCode || 'BW',
    allocatedCount: simulationCodes,
    totalCapacity: 100000,
  };
}

const OUTCOMES = [
  'COMPLETED', 'COMPLETED', 'COMPLETED', 'COMPLETED', 'COMPLETED', 'TERMINATED_BY_USER',
  'COMPLETED', 'TIMEOUT', 'COMPLETED', 'TERMINATED_BY_NETWORK', 'COMPLETED', 'ERROR',
];

const usageRand = stream(1901);

/** USSD sessions over the last 30 days (core/ussd-usage-records), newest first. */
export const MOCK_USSD_USAGE = Array.from({ length: 260 }, (_, i) => {
  const app = MOCK_USSD_APPS.filter((candidate) => candidate.enabled)[i % 5];
  const startMs = NOW - usageRand.int(2, 30 * 24 * 60) * MINUTE;
  const outcome = usageRand.pick(OUTCOMES);
  const hops = outcome === 'ERROR' ? 1 : usageRand.int(2, 7);
  const durationMs = outcome === 'TIMEOUT' ? 180000 : hops * usageRand.int(4000, 16000);
  const msisdn = phone(20000 + usageRand.int(0, 59999)).replace(/^\+/, '');
  return {
    id: i + 1,
    sessionId: `sess-${(startMs / 1000).toFixed(0)}-${pad4(i + 1)}`,
    applicationId: app.id,
    applicationName: app.name,
    serviceCode: app.serviceCode,
    mno: carrierOf(`+${msisdn}`).operatorName,
    msisdn,
    sessionStart: iso(startMs),
    sessionEnd: iso(startMs + durationMs),
    durationMs,
    hops,
    outcome,
    usageStatus: outcome === 'COMPLETED' ? 'BILLED' : outcome === 'ERROR' ? 'NOT_BILLABLE' : 'RATED',
    organisationId: MOCK_ORG_ID,
  };
}).sort((a, b) => b.sessionStart.localeCompare(a.sessionStart));

export function ussdUsage(params: URLSearchParams) {
  const from = params.get('from');
  const to = params.get('to');
  const fromMs = from ? Date.parse(from) : -Infinity;
  const toMs = to ? Date.parse(to) : Infinity;
  return MOCK_USSD_USAGE.filter((record) => {
    const at = Date.parse(record.sessionStart);
    return at >= fromMs && at <= toMs;
  });
}

export function ussdSessionDetail(sessionId: string) {
  const record = MOCK_USSD_USAGE.find((candidate) => candidate.sessionId === sessionId);
  if (!record) return undefined;
  const billable = record.usageStatus !== 'NOT_BILLABLE';
  return {
    usageRecord: record,
    ratedUsages: [
      { type: 'COST', amount: billable ? round1(record.hops * 0.12) : 0, currency: 'BWP' },
      { type: 'REVENUE', amount: billable ? round1(record.hops * 0.2) : 0, currency: 'BWP' },
    ],
  };
}

export const USSD_TELCOS: Record<string, { name: string; code: string; mcc: string; mnc: string; countryCode: string }[]> = {
  BW: CARRIERS.map((carrier) => ({ name: carrier.carrierName, code: carrier.operatorName, mcc: '652', mnc: carrier.mnc, countryCode: 'BW' })),
  ZA: [
    { name: 'Vodacom', code: 'VODACOM', mcc: '655', mnc: '01', countryCode: 'ZA' },
    { name: 'MTN', code: 'MTN', mcc: '655', mnc: '10', countryCode: 'ZA' },
  ],
  NA: [{ name: 'MTC', code: 'MTC', mcc: '649', mnc: '01', countryCode: 'NA' }],
};

export const USSD_COUNTRIES = [
  { code: 'BW', name: 'Botswana', dialCode: '+267' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27' },
  { code: 'NA', name: 'Namibia', dialCode: '+264' },
];

/** Where each simulator session is in its menu. */
const USSD_SESSIONS = new Map<string, string[]>();

interface UssdScreen {
  text: string;
  end?: boolean;
}

/** Scripted menus for the fixture applications; `path` is the user's inputs so far. */
function ussdScreen(app: MockUssdApp, path: string[]): UssdScreen | null {
  const [first, second, third] = path;
  switch (app.alias) {
    case 'soka-trivia': {
      const menu = 'SOKA Trivia\n1. Today\'s question\n2. My entries\n3. Top 5 this week\n4. How it works\n0. Exit';
      if (!path.length) return { text: menu };
      if (first === '0') return { text: 'Thank you for playing SOKA Trivia.', end: true };
      if (first === '1') {
        if (!second) return { text: 'What is the capital of Botswana?\n1. Francistown\n2. Gaborone\n3. Maun' };
        if (second === '2') return { text: 'Correct. You earned 1 entry into Friday\'s P5,000 draw. You now have 13 entries.', end: true };
        if (second === '1' || second === '3') return { text: 'Not quite. The answer is Gaborone. A new question is ready tomorrow.', end: true };
        return null;
      }
      if (first === '2') return second === '0' ? { text: menu } : { text: 'You have 12 entries this week. The draw is on Friday at 20:00.\n0. Back' };
      if (first === '3') return second === '0' ? { text: menu } : { text: 'Top 5 this week\n1. Kagiso M. 48\n2. Naledi S. 45\n3. Tebogo K. 41\n4. Mpho M. 39\n5. Lesego T. 37\n0. Back' };
      if (first === '4') return { text: 'Answer one question a day. Each correct answer is one entry into Friday\'s draw. Sessions are free.', end: true };
      return null;
    }
    case 'zebras-predictor': {
      if (!path.length) return { text: 'Zebras v Uganda, 15 Nov\n1. Predict the score\n2. My prediction\n0. Exit' };
      if (first === '0') return { text: 'Goodbye. Dial *150*10# to predict again.', end: true };
      if (first === '2') return { text: 'No prediction yet. Dial again and choose 1 to predict the score.', end: true };
      if (first !== '1') return null;
      if (!second) return { text: 'How many goals will the Zebras score? Enter 0 to 9' };
      if (!/^\d$/.test(second)) return null;
      if (!third) return { text: 'How many goals will Uganda score? Enter 0 to 9' };
      if (!/^\d$/.test(third)) return null;
      return { text: `Saved: Zebras ${second} - ${third} Uganda. Winners get an SMS after the match.`, end: true };
    }
    case 'balance-entries': {
      if (!path.length) return { text: 'Balance and Entries\n1. Draw entries\n2. Winnings\n0. Exit' };
      if (first === '1') return { text: 'You have 12 entries for Friday\'s draw.', end: true };
      if (first === '2') return { text: 'You have won P150 airtime this month. It was added to your balance.', end: true };
      if (first === '0') return { text: 'Goodbye.', end: true };
      return null;
    }
    case 'rollers-codes': {
      const clubs = ['Township Rollers', 'Gaborone United', 'Jwaneng Galaxy'];
      if (!path.length) return { text: `Back your club\n${clubs.map((club, k) => `${k + 1}. ${club}`).join('\n')}\n0. Exit` };
      if (first === '0') return { text: 'Goodbye.', end: true };
      const club = clubs[Number(first) - 1];
      return club ? { text: `You now back ${club}. Your entries count double this week.`, end: true } : null;
    }
    case 'customer-survey': {
      if (!path.length) return { text: 'How likely are you to recommend us to a friend? Reply 0 to 10' };
      if (!/^(10|\d)$/.test(first)) return null;
      return { text: 'Thank you. Your answer helps us improve.', end: true };
    }
    default: {
      if (!path.length) return { text: `${app.name}\n1. Continue\n0. Exit` };
      return { text: 'Thank you. This test session has ended.', end: true };
    }
  }
}

/** POST .../ussd/simulator/request: UssdServiceResponse for a scripted session. */
export function ussdSimulate(body: any) {
  const sessionId = String(body?.sessionId ?? '');
  const code = String(body?.serviceCode ?? '').replace(/\s+/g, '');
  const base = { sessionId, msisdn: body?.msisdn, operator: body?.operator, operatorCountryCode: body?.operatorCountryCode };
  const app = MOCK_USSD_APPS.find((candidate) => candidate.serviceCode === code);

  if (body?.serviceRequestType === 'Release' || body?.serviceRequestType === 'Timeout') {
    USSD_SESSIONS.delete(sessionId);
    return { ...base, serviceRequestType: 'Release', serviceData: '', metadata: {} };
  }
  if (!app) {
    return { ...base, serviceRequestType: 'Release', serviceData: `No application uses ${code || 'that code'}. Check the code and dial again.`, metadata: { 'error.occurred': true } };
  }
  if (!app.enabled) {
    return { ...base, serviceRequestType: 'Release', serviceData: `${app.name} is switched off. Enable it to test it here.`, metadata: { 'error.occurred': true } };
  }

  const path = body?.serviceRequestType === 'New' ? [] : [...(USSD_SESSIONS.get(sessionId) ?? [])];
  const input = String(body?.serviceData ?? '').trim();
  if (body?.serviceRequestType !== 'New') path.push(input);
  let screen = ussdScreen(app, path);
  if (!screen) {
    path.pop();
    const current = ussdScreen(app, path) ?? { text: '' };
    screen = { text: `That option is not on the menu.\n${current.text}` };
  }
  // A "Back" option returns to the main menu, so forget the path.
  if (!screen.end && path.length && path[path.length - 1] === '0') path.length = 0;
  if (screen.end) USSD_SESSIONS.delete(sessionId);
  else USSD_SESSIONS.set(sessionId, path);

  return {
    ...base,
    serviceRequestType: screen.end ? 'Release' : 'Continue',
    serviceData: screen.text,
    metadata: { applicationId: app.id, environment: app.environment },
  };
}

/** POST v1/organization/{org}/recipient-batches: a pre-uploaded number list. */
export function recipientBatch(body: any) {
  const numbers: string[] = Array.isArray(body?.phoneNumbers) ? body.phoneNumbers : [];
  const batchId = `batch-${hashOf(numbers.slice(0, 50).join(',') + numbers.length).toString(16)}`;
  RECIPIENT_BATCHES.set(batchId, new Set(numbers).size);
  return { batchId, count: numbers.length, expiresAt: iso(Date.now() + DAY) };
}
